import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import Dashboard from './Dashboard';
import UserProfile from './UserProfile';
import Settings from './Settings';
import OverviewMap from '../components/OverviewMap';
import RouteMap from '../components/RouteMap';
import { positionOrNull } from '../lib/melqarto';
import MobilityCard from '../components/MobilityCard';
import { getAllVehicles, getVehiclesByOwner, getVehicleById, createVehicle, updateVehicle, deleteVehicle } from '../api/services/vehiclesService';
import { getRentalsByRenter, getRentalsByOwner, cancelRental } from '../api/services/rentalsService';
import { sendCustomNotification } from '../api/services/notificationsService';
import { ActionRow, Avatar, Band, BigButton, Card, CloseButton, FilterChip, ICONS, LineIcon, LinkButton, SectionLabel, StatusPill, Tone, VehicleThumb, formatDT } from '../components/ui/dg';
import { searchAdminRentals, personName, AdminRentalPerson } from '../api/services/adminRentalsService';
import { getAllReviews } from '../api/services/reviewsService';
import { getAllInspections } from '../api/services/inspectionsService';
import { VEHICLE_TYPE_LABELS } from '../viewmodels/InternalViewModel';
import { Rental, Vehicle } from '../api/services/api';
import { useAuth } from '../contexts/AuthContext';
import LocationPicker from '../components/LocationPicker';
import { isFavorite, toggleFavorite, getFavorites, removeFromFavorites, addToFavorites, getCityFromCoords } from '../utils/helpers';
import Modal from '../components/Modal';
import FleetView from './FleetView';
import ManagementUsers from './Management/Users';
import ManagementVerifications from './Management/Verifications';
import ManagementPendingQueue from './Management/PendingQueue';
import AccessDenied from '../components/AccessDenied';
import { canOpenSection } from '../lib/permissions';


import ManagementCommunicationHub from './Management/CommunicationHub';
import ManagementAnalyticsHub from './Management/AnalyticsHub';
import ManagementFees from './Management/Fees';
import ManagementOwnerPromos from './Management/OwnerPromos';
import ManagementSupport from './Management/Support';
import ManagementLogsHub from './Management/LogsHub';


/** Libellés des sections, pour l'écran d'accès refusé. */
const SECTION_LABELS: Record<string, string> = {
  dashboard: 'Tableau de bord',
  mytrips: 'Trajets',
  myrentals: 'Flotte',
  analytics: 'Analytics',
  frais: 'Frais',
  communication: 'Communication',
  support: 'Support',
  users: 'Utilisateurs',
  verifications: 'Vérifications',
  'a-traiter': 'À traiter',
  journaux: 'Journaux'
};

// Fonction pour détecter si l'appareil est mobile
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (window.innerWidth <= 768);
};

const OverviewView = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [currentScrollIndex, setCurrentScrollIndex] = useState(0);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [userPosition, setUserPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<any>(null);
  const [favorites, setFavorites] = useState<{ [key: string]: boolean }>({});
  const [isMobile, setIsMobile] = useState(false);

  // Récupérer les véhicules depuis l'API et la position de l'utilisateur
  useEffect(() => {
    // Obtenir la position de l'utilisateur
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setUserPosition({
            lat: pos.coords.latitude,
            lng: pos.coords.longitude
          });
          console.log('📍 Position utilisateur:', pos.coords.latitude, pos.coords.longitude);
        },
        (error) => {
          console.error('Erreur de géolocalisation:', error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    }

    const fetchVehicles = async () => {
      try {
        setLoading(true);
        if (token) {
          const vehiclesData = await getAllVehicles(token);
          console.log('🚗 Véhicules récupérés:', vehiclesData);
          console.log('🔍 Premier véhicule (exemple):', vehiclesData[0]);
          console.log('🔍 Propriétés du premier véhicule:', vehiclesData[0] ? Object.keys(vehiclesData[0]) : 'Aucun véhicule');
          setVehicles(vehiclesData);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des véhicules:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVehicles();
  }, [token]);

  // Détecter si l'appareil est mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(isMobileDevice());
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  // Fonction pour obtenir l'icône du véhicule
  const getVehicleIcon = (vehicleType: string) => {
    switch (vehicleType) {
      case 'car': return '🚗';
      case 'motorcycle': return '🏍️';
      case 'scooter': return '🛴';
      case 'bike': return '🚲';
      case 'quad': return '🏎️';
      default: return '🚗';
    }
  };

  // Fonction pour calculer la distance (simulation)
  const calculateDistance = (vehicleLat: number, vehicleLng: number, userLat: number, userLng: number) => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = (vehicleLat - userLat) * Math.PI / 180;
    const dLng = (vehicleLng - userLng) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(userLat * Math.PI / 180) * Math.cos(vehicleLat * Math.PI / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;
    return distance < 1 ? `${Math.round(distance * 1000)}m` : `${distance.toFixed(1)}km`;
  };

  // Véhicules avec coordonnées valides pour la carte
  const mapVehicles = vehicles.filter(vehicle =>
    vehicle.latitude && vehicle.longitude &&
    !isNaN(vehicle.latitude) && !isNaN(vehicle.longitude)
  ).map(vehicle => ({
    ...vehicle,
    type: vehicle.vehicle_type // Ajouter le champ type pour la carte
  }));

  // Fonction pour calculer la distance en km (pour le tri)
  const calculateDistanceKm = (vehicleLat: number, vehicleLng: number, userLat: number, userLng: number) => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = (vehicleLat - userLat) * Math.PI / 180;
    const dLng = (vehicleLng - userLng) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(userLat * Math.PI / 180) * Math.cos(vehicleLat * Math.PI / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Tous les véhicules pour la liste (même sans coordonnées)
  const cards = vehicles.map((vehicle) => {
    const distanceKm = (() => {
      // Vérifier si on a la position de l'utilisateur et les coordonnées du véhicule
      if (!userPosition || !vehicle.latitude || !vehicle.longitude ||
        isNaN(vehicle.latitude) || isNaN(vehicle.longitude)) {
        return Infinity; // Pour trier en dernier
      }
      return calculateDistanceKm(vehicle.latitude, vehicle.longitude, userPosition.lat, userPosition.lng);
    })();

    return {
      id: vehicle.id_vehicle || vehicle.id, // Utiliser id_vehicle en priorité
      id_vehicle: vehicle.id_vehicle || vehicle.id, // Ajouter explicitement
      title: vehicle.brand && vehicle.model ? `${vehicle.brand} ${vehicle.model}` : vehicle.serial_number || `Véhicule #${vehicle.id_vehicle || vehicle.id}`,
      type: vehicle.vehicle_type,
      serial_number: vehicle.serial_number,
      distance: (() => {
        // Vérifier si on a la position de l'utilisateur et les coordonnées du véhicule
        if (!userPosition || !vehicle.latitude || !vehicle.longitude ||
          isNaN(vehicle.latitude) || isNaN(vehicle.longitude)) {
          return 'N/D';
        }
        return calculateDistance(vehicle.latitude, vehicle.longitude, userPosition.lat, userPosition.lng);
      })(),
      distanceKm: distanceKm, // Pour le tri
      price: vehicle.hourly_rate ? `${Number(vehicle.hourly_rate).toFixed(2)}€/h` : 'Prix non défini',
      status: vehicle.is_available ? 'disponible' : 'occupé',
      icon: getVehicleIcon(vehicle.vehicle_type),
      latitude: vehicle.latitude,
      longitude: vehicle.longitude,
      owner_id: vehicle.owner_id || user?.id // Utiliser l'owner_id du véhicule ou l'utilisateur connecté
    };
  }).sort((a, b) => {
    // Trier par distance (du plus proche au plus loin)
    // Les véhicules sans coordonnées (distanceKm = Infinity) sont placés à la fin
    return a.distanceKm - b.distanceKm;
  });

  console.log('🗺️ Véhicules pour la carte:', mapVehicles.length, 'sur', vehicles.length, 'véhicules');
  console.log('📋 Cartes créées:', cards.length, 'sur', vehicles.length, 'véhicules');

  const handleVehicleSelect = (vehicle: any) => {
    setSelectedVehicle(vehicle);
    console.log('🚗 Véhicule sélectionné:', vehicle);
  };

  // Fonction pour gérer le clic sur le bouton favori
  const handleFavoriteClick = (vehicle: any, e: React.MouseEvent) => {
    e.stopPropagation(); // Empêcher la sélection du véhicule
    const vehicleId = vehicle.id_vehicle || vehicle.id || vehicle.serial_number || `vehicle-${vehicle.latitude}-${vehicle.longitude}`;

    // Toggle le favori
    const wasFavorite = favorites[vehicleId] || false;
    const newFavoriteState = !wasFavorite;

    if (newFavoriteState) {
      addToFavorites(vehicle);
    } else {
      removeFromFavorites(vehicleId);
    }

    // Mettre à jour l'état local
    setFavorites(prev => ({
      ...prev,
      [vehicleId]: newFavoriteState
    }));
  };

  // Charger l'état des favoris au montage
  useEffect(() => {
    const loadFavoritesState = () => {
      const favoritesState: { [key: string]: boolean } = {};
      vehicles.forEach(vehicle => {
        // Utiliser un identifiant unique pour chaque véhicule
        const vehicleId = vehicle.id_vehicle || vehicle.id || vehicle.serial_number || `vehicle-${vehicle.latitude}-${vehicle.longitude}`;
        // Vérifier si le véhicule est en favori dans localStorage
        favoritesState[vehicleId] = isFavorite(vehicleId);
      });
      setFavorites(favoritesState);
    };

    if (vehicles.length > 0) {
      loadFavoritesState();
    }
  }, [vehicles]);

  return (
    <main className="flex-1 relative overflow-hidden">
      {/* Carte principale */}
      <OverviewMap
        vehicles={mapVehicles}
        userPosition={userPosition || undefined}
        onVehicleSelect={selectedVehicle}
      />

      {/* Overlay des cartes en bas */}
      <div className={`absolute left-4 right-4 z-10 ${
        isMobile 
          ? 'bottom-4' // Position plus basse sur mobile pour éviter la barre du navigateur
          : 'bottom-20'
      }`}
      style={{
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom)' : '0'
      }}>
        <div className="bg-white/0 backdrop-blur-sm rounded-lg border border-gray-200 shadow-lg p-4">
          <div className="mb-1">
            <h3 className="text-xs font-semibold text-gray-800">Mobilités disponibles</h3>
            <p className="text-xs text-gray-600 mb-2">Sélectionnez un véhicule pour commencer votre trajet</p>
          </div>
          {/* Liste horizontale défilante */}
          {loading ? (
            <div className="flex space-x-3">
              {[1, 2, 3].map((index) => (
                <div key={index} className="flex-shrink-0 w-56 bg-white rounded-lg shadow-md p-3">
                  <div className="animate-pulse">
                    <div className="h-24 bg-gray-300 rounded-t-lg mb-2"></div>
                    <div className="h-4 bg-gray-300 rounded mb-1"></div>
                    <div className="h-3 bg-gray-300 rounded w-3/4 mb-1"></div>
                    <div className="h-3 bg-gray-300 rounded w-1/2 mb-2"></div>
                    <div className="h-7 bg-gray-300 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : cards.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-gray-900 mb-1">Aucune mobilité disponible</h3>
              <p className="text-xs text-gray-500">Aucun véhicule n'est actuellement disponible dans votre zone</p>
            </div>
          ) : (
            <div
              className="flex space-x-3 overflow-x-auto pb-2 scrollbar-hide"
              onScroll={(e) => {
                const element = e.target as HTMLDivElement;
                const scrollLeft = element.scrollLeft;
                const cardWidth = 224; // w-56 = 224px
                const spacing = 12; // space-x-3 = 12px
                const totalCardWidth = cardWidth + spacing;
                const maxScrollLeft = element.scrollWidth - element.clientWidth;
                const scrollProgress = scrollLeft / maxScrollLeft;
                const newIndex = Math.round(scrollProgress * (cards.length - 1));
                setCurrentScrollIndex(Math.max(0, Math.min(newIndex, cards.length - 1)));
              }}
            >
              {cards.map((card, index) => (
                <MobilityCard
                  key={`vehicle-${card.id || index}-${card.latitude}-${card.longitude}`}
                  vehicle={{
                    id_vehicle: card.id,
                    id: card.id,
                    brand: card.title.split(' ')[0],
                    model: card.title.split(' ').slice(1).join(' '),
                    serial_number: card.serial_number,
                    vehicle_type: card.type,
                    hourly_rate: parseFloat(card.price.replace('€/h', '')),
                    is_available: card.status === 'disponible',
                    latitude: card.latitude,
                    longitude: card.longitude,
                    owner_id: card.owner_id
                  }}
                  cities={{}}
                  onEdit={(vehicle) => {
                    navigate('/booking', {
                      state: {
                        vehicle: {
                          id: vehicle.id,
                          id_vehicle: vehicle.id,
                          title: card.title,
                          type: card.type,
                          price: card.price,
                          distance: card.distance,
                          status: card.status,
                          icon: card.icon,
                          vehicle_type: card.type,
                          hourly_rate: parseFloat(card.price.replace('€/h', '')),
                          owner_id: vehicle.owner_id || user?.id
                        },
                        fromPage: 'explorer'
                      }
                    });
                  }}
                  onDelete={(vehicleId) => {
                    console.log('Suppression du véhicule:', vehicleId);
                  }}
                  getVehicleIcon={getVehicleIcon}
                  getVehicleTypeInFrench={(vehicleType) => {
                    switch (vehicleType) {
                      case 'car': return 'Voiture';
                      case 'motorcycle': return 'Moto';
                      case 'scooter': return 'Scooter';
                      case 'bike': return 'Vélo';
      case 'quad': return 'Quad';
                      default: return 'Véhicule';
                    }
                  }}
                  buttonText="Réserver maintenant"
                  favorites={favorites}
                  onFavoriteClick={handleFavoriteClick}
                  showFavoriteButton={true}
                  onVehicleSelect={handleVehicleSelect}
                  userPosition={userPosition || undefined}
                  distance={card.distance}
                />
              ))}
            </div>
          )}

          {/* Indicateurs */}
          <div className="flex justify-center space-x-2 mt-0.5">
            {cards.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  const element = document.querySelector('.scrollbar-hide') as HTMLDivElement;
                  if (element) {
                    const cardWidth = 224; // w-56 = 224px
                    const spacing = 12; // space-x-3 = 12px
                    const totalCardWidth = cardWidth + spacing;
                    element.scrollLeft = index * totalCardWidth;
                    setCurrentScrollIndex(index);
                  }
                }}
                className={`w-2 h-2 rounded-full transition-all duration-200 ${currentScrollIndex === index
                    ? 'bg-purple-600 w-6'
                    : 'bg-gray-300 hover:bg-gray-400'
                  }`}
              />
            ))}
          </div>
        </div>
      </div>
    </main>
  );
};

const DiscoverView = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [userVehicles, setUserVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<any>(null);
  const [cities, setCities] = useState<{ [key: string]: string }>({});
  const [vehicleForm, setVehicleForm] = useState({
    serial_number: '',
    vehicle_type: 'car',
    brand: '',
    model: '',
    year: '',
    is_platform_owned: false,
    is_available: true,
    hourly_rate: '',
    latitude: '',
    longitude: ''
  });

  // Charger les véhicules de l'utilisateur
  useEffect(() => {
    const loadUserVehicles = async () => {
      try {
        setLoading(true);
        const userAny = user as any;
        const userId = userAny?.id || userAny?.id_user || userAny?.user_id || userAny?.userId;

        const vehiclesData = await getVehiclesByOwner(userId, token ?? "");
        console.log('🚗 Véhicules de l\'utilisateur récupérés DiscoverView:', vehiclesData);
        setUserVehicles(vehiclesData || []);

      } catch (error) {
        console.error('Erreur lors du chargement des véhicules:', error);
      } finally {
        setLoading(false);
      }
    };

    loadUserVehicles();
  }, [token, user?.id]);

  // Fonction pour charger les villes des véhicules
  const loadVehicleCities = async (vehicles: any[]) => {
    console.log('🏙️ Chargement des villes pour', vehicles.length, 'véhicules');
    for (const vehicle of vehicles) {
      if (vehicle.latitude && vehicle.longitude) {
        const cityKey = `${vehicle.latitude},${vehicle.longitude}`;
        if (!cities[cityKey]) {
          try {
            const city = await getCityFromCoords(Number(vehicle.latitude), Number(vehicle.longitude));
            // Mettre à jour immédiatement chaque ville trouvée
            setCities(prev => ({ ...prev, [cityKey]: city }));
            console.log('✅ Ville chargée:', city);
            // Délai de 1 seconde entre les requêtes pour éviter les erreurs CORS/403
            await new Promise(resolve => setTimeout(resolve, 1000));
          } catch (error) {
            console.error('❌ Erreur lors du chargement de la ville:', error);
            // En cas d'erreur, on n'affiche pas "Ville inconnue" mais on laisse les coordonnées
            // setCities(prev => ({ ...prev, [cityKey]: 'Ville inconnue' }));
          }
        }
      }
    }
  };

  // Charger les villes quand les véhicules changent
  useEffect(() => {
    if (userVehicles.length > 0) {
      // Charger les villes en arrière-plan sans bloquer l'interface
      loadVehicleCities(userVehicles).catch(console.error);
    }
  }, [userVehicles]);

  // Log l'état du modal
  useEffect(() => {
    console.log('🔍 DiscoverView - showAddVehicle changé:', showAddVehicle);
  }, [showAddVehicle]);

  // Fonction pour obtenir l'icône du véhicule
  const getVehicleIcon = (vehicleType: string) => {
    switch (vehicleType) {
      case 'car': return '🚗';
      case 'motorcycle': return '🏍️';
      case 'scooter': return '🛴';
      case 'bike': return '🚲';
      case 'quad': return '🏎️';
      default: return '🚗';
    }
  };

  // Fonction pour obtenir le type de véhicule en français
  const getVehicleTypeInFrench = (vehicleType: string) => {
    switch (vehicleType) {
      case 'car': return 'Voiture';
      case 'motorcycle': return 'Moto';
      case 'scooter': return 'Scooter';
      case 'bike': return 'Vélo';
      case 'quad': return 'Quad';
      default: return 'Véhicule';
    }
  };

  // Fonction pour ouvrir le formulaire d'ajout
  const openAddVehicle = () => {
    setVehicleForm({
      serial_number: '',
      vehicle_type: 'car',
      brand: '',
      model: '',
      year: '',
      is_platform_owned: false,
      is_available: true,
      hourly_rate: '',
      latitude: '',
      longitude: ''
    });
    setEditingVehicle(null);
    setShowAddVehicle(true);
  };

  // Fonction pour ajouter un véhicule
  const addVehicle = async () => {
    if (!token || !user?.id) return;

    try {
      const userAny = user as any;
      const userId = userAny?.id || userAny?.id_user || userAny?.user_id || userAny?.userId;

      const vehicleData = {
        serial_number: vehicleForm.serial_number,
        vehicle_type: vehicleForm.vehicle_type,
        brand: vehicleForm.brand,
        model: vehicleForm.model,
        year: vehicleForm.year,
        is_platform_owned: vehicleForm.is_platform_owned,
        is_available: vehicleForm.is_available,
        hourly_rate: parseFloat(vehicleForm.hourly_rate) || 0,
        latitude: vehicleForm.latitude ? parseFloat(vehicleForm.latitude) : null,
        longitude: vehicleForm.longitude ? parseFloat(vehicleForm.longitude) : null,
        owner_id: userId.toString()
      };

      const newVehicle = await createVehicle(vehicleData, token);
      setUserVehicles(prev => [...prev, newVehicle]);
      closeVehicleForm();
      console.log('✅ Véhicule ajouté avec succès:', newVehicle);
    } catch (error) {
      console.error('❌ Erreur lors de l\'ajout du véhicule:', error);
    }
  };

  // Fonction pour ouvrir le formulaire de modification
  const openEditVehicle = (vehicle: any) => {
    setVehicleForm({
      serial_number: vehicle.serial_number || '',
      vehicle_type: vehicle.vehicle_type || 'car',
      brand: vehicle.brand || '',
      model: vehicle.model || '',
      year: vehicle.year || '',
      is_platform_owned: vehicle.is_platform_owned || false,
      is_available: vehicle.is_available === true,
      hourly_rate: vehicle.hourly_rate || '',
      latitude: vehicle.latitude || '',
      longitude: vehicle.longitude || ''
    });
    setEditingVehicle(vehicle);
    setShowAddVehicle(true);
  };

  // Fonction pour sauvegarder un véhicule (ajout ou modification)
  const saveVehicle = async () => {

    try {
      const userAny = user as any;
      const userId = userAny?.id || userAny?.id_user || userAny?.user_id || userAny?.userId;

      const vehicleData = {
        serial_number: vehicleForm.serial_number,
        vehicle_type: vehicleForm.vehicle_type,
        brand: vehicleForm.brand,
        model: vehicleForm.model,
        year: vehicleForm.year,
        is_platform_owned: vehicleForm.is_platform_owned,
        is_available: vehicleForm.is_available,
        hourly_rate: parseFloat(vehicleForm.hourly_rate) || 0,
        latitude: vehicleForm.latitude ? parseFloat(vehicleForm.latitude) : null,
        longitude: vehicleForm.longitude ? parseFloat(vehicleForm.longitude) : null,
        owner_id: userId.toString()
      };

      if (editingVehicle) {
        // Modifier un véhicule existant
        const vehicleId = editingVehicle.id_vehicle || editingVehicle.id;
        console.log('🔧 Modification du véhicule ID:', vehicleId);
        console.log('🔧 Données à envoyer:', vehicleData);

        const updatedVehicle = await updateVehicle(vehicleId, vehicleData, token ?? "");
        console.log('✅ Réponse API complète:', updatedVehicle);
        console.log('🔍 ID du véhicule modifié:', updatedVehicle.id_vehicle || updatedVehicle.id);

        setUserVehicles(prev => {
          console.log('🔄 Véhicules avant modification:', prev.map(v => ({ id: v.id_vehicle || v.id, serial: v.serial_number })));
          console.log('🎯 Véhicule à modifier:', { id: editingVehicle.id_vehicle || editingVehicle.id, serial: editingVehicle.serial_number });

          const updated = prev.map(v => {
            // Vérifier si c'est le bon véhicule à modifier
            const vehicleId = v.id_vehicle || v.id;
            const editingId = editingVehicle.id_vehicle || editingVehicle.id;

            console.log('🔍 Comparaison:', {
              vehicleId,
              editingId,
              vehicleSerial: v.serial_number,
              editingSerial: editingVehicle.serial_number,
              isMatch: vehicleId === editingId
            });

            if (vehicleId === editingId) {
              console.log('✅ Véhicule trouvé et modifié:', vehicleId);
              return updatedVehicle;
            }
            return v;
          });
          console.log('🔄 Liste mise à jour:', updated.length, 'véhicules');
          return updated;
        });
        console.log('✅ Véhicule modifié avec succès:', updatedVehicle);
      } else {
        // Ajouter un nouveau véhicule
        const newVehicle = await createVehicle(vehicleData, token ?? "");
        setUserVehicles(prev => [...prev, newVehicle]);
        console.log('✅ Véhicule ajouté avec succès:', newVehicle);
      }

      closeVehicleForm();
    } catch (error) {
      console.error('❌ Erreur lors de la sauvegarde du véhicule:', error);
    }
  };

  // Fonction pour supprimer un véhicule
  const deleteVehicleHandler = async (vehicleId: string | number) => {
    //if (!token) return;

    try {
      // `Number()` sur un UUID vaut NaN : la requête partait sur
      // /api/vehicles/NaN, que l'API ne pouvait que refuser.
      await deleteVehicle(String(vehicleId), token ?? "");
      setUserVehicles(prev => prev.filter(v => v.id_vehicle !== vehicleId && v.id !== vehicleId));
      console.log('✅ Véhicule supprimé avec succès');
    } catch (error) {
      console.error('❌ Erreur lors de la suppression du véhicule:', error);
    }
  };

  // Fonction pour fermer le formulaire
  const closeVehicleForm = () => {
    setShowAddVehicle(false);
    setEditingVehicle(null);
    setVehicleForm({
      serial_number: '',
      vehicle_type: 'car',
      brand: '',
      model: '',
      year: '',
      is_platform_owned: false,
      is_available: true,
      hourly_rate: '',
      latitude: '',
      longitude: ''
    });
  };

  return (
    <main className="flex-1 relative overflow-hidden">
      {/* Carte principale */}
      <OverviewMap
        vehicles={userVehicles.filter(vehicle =>
          vehicle.latitude && vehicle.longitude &&
          !isNaN(vehicle.latitude) && !isNaN(vehicle.longitude)
        ).map(vehicle => ({
          ...vehicle,
          type: vehicle.vehicle_type
        }))}
        userPosition={undefined}
        onVehicleSelect={() => { }}
      />

      {/* Overlay des cartes en bas */}
      <div className="absolute bottom-4 left-4 right-4 z-10">
        <div className="bg-white/0 backdrop-blur-sm rounded-lg border border-gray-200 shadow-lg p-4">
          <div className="mb-1">
            <h3 className="text-xs font-semibold text-gray-800">Ma Flotte</h3>
            <p className="text-xs text-gray-600 mb-2">Gérez vos véhicules personnels</p>
          </div>

          {/* Liste horizontale défilante */}
          {loading ? (
            <div className="flex space-x-3">
              {[1, 2, 3].map((index) => (
                <div key={index} className="flex-shrink-0 w-56 bg-white rounded-lg shadow-md p-3">
                  <div className="animate-pulse">
                    <div className="h-24 bg-gray-300 rounded-t-lg mb-2"></div>
                    <div className="h-4 bg-gray-300 rounded mb-1"></div>
                    <div className="h-3 bg-gray-300 rounded w-3/4 mb-1"></div>
                    <div className="h-3 bg-gray-300 rounded w-1/2 mb-2"></div>
                    <div className="h-7 bg-gray-300 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : userVehicles.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-sm font-medium text-gray-900 mb-1">Aucun véhicule</h3>
              <p className="text-xs text-gray-500">Commencez par ajouter votre premier véhicule à votre flotte</p>
              <button
                onClick={openAddVehicle}
                className="mt-3 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
              >
                Ajouter un véhicule
              </button>
              <button
                onClick={() => {
                  console.log('🔄 Forçage du chargement des véhicules');
                  const loadUserVehicles = async () => {

                    try {
                      setLoading(true);
                      const userAny = user as any;
                      const userId = userAny?.id || userAny?.id_user || userAny?.user_id || userAny?.userId;
                      console.log('🆔 User ID utilisé:', userId);

                      const userVehicles = await getVehiclesByOwner(userId.toString(), token ?? "");
                      console.log('✅ Véhicules récupérés:', userVehicles);

                      if (Array.isArray(userVehicles)) {
                        setUserVehicles(userVehicles);
                        console.log('📊 Nombre de véhicules chargés:', userVehicles.length);
                      } else {
                        console.log('⚠️ Réponse non-array:', userVehicles);
                        setUserVehicles([]);
                      }
                    } catch (error) {
                      console.error('❌ Erreur lors du chargement des véhicules:', error);
                      setUserVehicles([]);
                    } finally {
                      setLoading(false);
                    }
                  };
                  loadUserVehicles();
                }}
                className="mt-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                🔄 Forcer le chargement
              </button>
            </div>
          ) : (
            <div
              className="flex space-x-3 overflow-x-auto pb-2 scrollbar-hide"
              onScroll={(e) => {
                const element = e.target as HTMLDivElement;
                const scrollLeft = element.scrollLeft;
                const cardWidth = 224; // w-56 = 224px
                const spacing = 12; // space-x-3 = 12px
                const totalCardWidth = cardWidth + spacing;
                const maxScrollLeft = element.scrollWidth - element.clientWidth;
                const scrollProgress = scrollLeft / maxScrollLeft;
                const newIndex = Math.round(scrollProgress * (userVehicles.length - 1));
                // setCurrentScrollIndex(Math.max(0, Math.min(newIndex, userVehicles.length - 1)));
              }}
            >
              {userVehicles.map((vehicle, index) => {
                const icon = getVehicleIcon(vehicle.vehicle_type);
                const status = vehicle.is_available ? 'disponible' : 'occupé';
                const title = vehicle.brand && vehicle.model ? `${vehicle.brand} ${vehicle.model}` : vehicle.serial_number || `Véhicule #${vehicle.id_vehicle || vehicle.id}`;
                const price = vehicle.hourly_rate ? `${Number(vehicle.hourly_rate).toFixed(2)}€/h` : 'Prix non défini';

                return (
                  <div
                    key={`vehicle-${vehicle.id_vehicle || vehicle.id}-${index}`}
                    className="flex-shrink-0 w-56 rounded-lg shadow-md transition-all duration-200 mobility-card bg-white hover:shadow-lg cursor-pointer"
                  >
                    {/* Image du véhicule */}
                    <div className={`relative h-24 rounded-t-lg flex items-center justify-center ${status === 'disponible'
                        ? 'bg-gradient-to-br from-green-100 to-blue-100'
                        : 'bg-gradient-to-br from-gray-200 to-gray-300'
                      }`}>
                      <div className={`text-3xl ${status !== 'disponible' ? 'opacity-50' : ''}`}>
                        {icon}
                      </div>
                      {/* Tag de statut */}
                      <div className={`absolute top-2 right-2 px-2 py-1 rounded-full shadow-md text-xs font-semibold ${vehicle.is_available
                          ? 'bg-green-100 text-green-800'
                          : 'bg-orange-100 text-orange-800'
                        }`}>
                        {vehicle.is_available ? 'Disponible' : 'Occupé'}
                      </div>
                      {/* Bouton de suppression */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (window.confirm('Êtes-vous sûr de vouloir supprimer ce véhicule ?')) {
                            deleteVehicleHandler(vehicle.id_vehicle || vehicle.id);
                          }
                        }}
                        className="absolute top-2 left-2 w-6 h-6 rounded-full shadow-md flex items-center justify-center transition-colors bg-red-100 hover:bg-red-200"
                        title="Supprimer le véhicule"
                      >
                        <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>

                    {/* Contenu de la carte */}
                    <div className="p-2">
                      {/* Titre */}
                      <div className="flex items-start justify-between mb-1">
                        <h4 className="font-semibold text-gray-900 text-sm leading-tight flex-1">{title}</h4>
                      </div>

                      {/* Informations détaillées */}
                      <div className="space-y-0.5 text-xs text-gray-600 mb-2">
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">Type: {getVehicleTypeInFrench(vehicle.vehicle_type)}</span>
                          <span className="font-semibold text-green-600">{price}</span>
                        </div>

                        <div className="flex items-center justify-between">
                          <span className="text-gray-500">ID: {vehicle.serial_number ? vehicle.serial_number.slice(-6) : vehicle.id_vehicle || vehicle.id}</span>
                          <span className="text-gray-500">
                            {vehicle.latitude && vehicle.longitude
                              ? (cities[`${vehicle.latitude},${vehicle.longitude}`] || `${Number(vehicle.latitude).toFixed(2)}, ${Number(vehicle.longitude).toFixed(2)}`)
                              : 'N/D'
                            }
                          </span>
                        </div>
                      </div>

                      {/* Bouton de gestion */}
                      <button
                        onClick={() => {
                          console.log('🖱️ Bouton "Gérer" cliqué !');
                          console.log('🚗 Véhicule:', vehicle);
                          openEditVehicle(vehicle);
                        }}
                        className="w-full py-1.5 px-4 rounded-lg transition-colors text-sm font-medium bg-green-600 text-white hover:bg-green-700 cursor-pointer">
                        Gérer
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Indicateurs */}
          <div className="flex justify-center space-x-2 mt-0.5">
            {userVehicles.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  const element = document.querySelector('.scrollbar-hide') as HTMLDivElement;
                  if (element) {
                    const cardWidth = 224; // w-56 = 224px
                    const spacing = 12; // space-x-3 = 12px
                    const totalCardWidth = cardWidth + spacing;
                    element.scrollLeft = index * totalCardWidth;
                  }
                }}
                className="w-2 h-2 rounded-full transition-all duration-200 bg-gray-300 hover:bg-gray-400"
              />
            ))}
          </div>
        </div>
      </div>

      {/* Modal pour ajouter un véhicule */}
      {showAddVehicle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingVehicle ? 'Modifier le véhicule' : 'Ajouter un véhicule'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de série</label>
                <input
                  type="text"
                  value={vehicleForm.serial_number}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, serial_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Numéro de série"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type de véhicule</label>
                <select
                  value={vehicleForm.vehicle_type}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, vehicle_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="car">Voiture</option>
                  <option value="motorcycle">Moto</option>
                  <option value="scooter">Scooter</option>
                  <option value="bike">Vélo</option>
                  <option value="quad">Quad</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Marque</label>
                  <input
                    type="text"
                    value={vehicleForm.brand}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, brand: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Marque"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Modèle</label>
                  <input
                    type="text"
                    value={vehicleForm.model}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, model: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="Modèle"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Année</label>
                  <input
                    type="number"
                    value={vehicleForm.year}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, year: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="2024"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tarif horaire (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={vehicleForm.hourly_rate}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, hourly_rate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder="10.00"
                  />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_available"
                  checked={vehicleForm.is_available}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, is_available: e.target.checked })}
                  className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                />
                <label htmlFor="is_available" className="text-sm text-gray-700">
                  Disponible pour la location
                </label>
              </div>
            </div>

            <div className="flex space-x-3 mt-6">
              <button
                onClick={closeVehicleForm}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={() => {
                  console.log('🖱️ Bouton "Modifier/Ajouter" cliqué !');
                  console.log('🔍 editingVehicle:', editingVehicle);
                  console.log('🔍 vehicleForm:', vehicleForm);
                  saveVehicle();
                }}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                {editingVehicle ? 'Modifier' : 'Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

const FavoritesView = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState<{ [key: string]: string }>({});
  const [removingFavorites, setRemovingFavorites] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    const loadFavorites = () => {
      const favs = getFavorites();
      setFavorites(favs);
      setLoading(false);
    };

    loadFavorites();
  }, []);

  // Synchroniser les favoris avec les données en temps réel
  useEffect(() => {
    const syncFavoritesWithRealTimeData = () => {
      const favs = getFavorites();
      if (favs.length > 0) {
        // Récupérer les données en temps réel depuis l'API
        getAllVehicles().then(realTimeVehicles => {
          const updatedFavorites = favs.map(fav => {
            // Chercher le véhicule correspondant dans les données en temps réel
            const realTimeVehicle = realTimeVehicles.find((rtv: any) => {
              const favId = fav.id || fav.serial_number || `vehicle-${fav.latitude}-${fav.longitude}`;
              const rtvId = rtv.id || rtv.serial_number || `vehicle-${rtv.latitude}-${rtv.longitude}`;
              return favId === rtvId;
            });

            // Si trouvé, utiliser les données en temps réel, sinon garder les données locales
            return realTimeVehicle ? { ...fav, ...realTimeVehicle } : fav;
          });

          setFavorites(updatedFavorites);
        }).catch(error => {
          console.error('Erreur lors de la synchronisation des favoris:', error);
        });
      }
    };

    // Synchroniser toutes les 30 secondes
    const interval = setInterval(syncFavoritesWithRealTimeData, 30000);

    // Synchroniser immédiatement au montage
    syncFavoritesWithRealTimeData();

    return () => clearInterval(interval);
  }, []);

  // Charger les noms de villes pour les favoris
  useEffect(() => {
    const loadCities = async () => {
      const citiesData: { [key: string]: string } = {};

      for (const vehicle of favorites) {
        if (vehicle.latitude && vehicle.longitude) {
          try {
            const city = await getCityFromCoords(Number(vehicle.latitude), Number(vehicle.longitude));
            citiesData[`${vehicle.latitude},${vehicle.longitude}`] = city;
          } catch (error) {
            console.error('Erreur lors du chargement de la ville:', error);
            citiesData[`${vehicle.latitude},${vehicle.longitude}`] = `${Number(vehicle.latitude).toFixed(2)}, ${Number(vehicle.longitude).toFixed(2)}`;
          }
        }
      }

      setCities(citiesData);
    };

    if (favorites.length > 0) {
      loadCities();
    }
  }, [favorites]);

  const handleRemoveFavorite = (vehicleId: string | number) => {
    // Marquer le véhicule comme en cours de suppression (pour l'animation)
    setRemovingFavorites(prev => ({ ...prev, [vehicleId]: true }));

    // Attendre l'animation puis supprimer
    setTimeout(() => {
      const success = removeFromFavorites(vehicleId);
      if (success) {
        setFavorites(prev => prev.filter(fav => {
          const favId = fav.id || fav.serial_number || `vehicle-${fav.latitude}-${fav.longitude}`;
          return favId !== vehicleId;
        }));
        // Retirer de l'état d'animation
        setRemovingFavorites(prev => {
          const newState = { ...prev };
          delete newState[vehicleId];
          return newState;
        });
      }
    }, 300); // Durée de l'animation
  };

  const getVehicleIcon = (vehicleType: string) => {
    switch (vehicleType) {
      case 'car': return '🚗';
      case 'motorcycle': return '🏍️';
      case 'scooter': return '🛴';
      case 'bike': return '🚲';
      case 'quad': return '🏎️';
      default: return '🚗';
    }
  };

  const getVehicleTypeInFrench = (vehicleType: string) => {
    switch (vehicleType) {
      case 'car': return 'Voiture';
      case 'motorcycle': return 'Moto';
      case 'scooter': return 'Trottinette';
      case 'bike': return 'Vélo';
      case 'quad': return 'Quad';
      default: return 'Véhicule';
    }
  };

  /* Squelette plutôt que spinner : l'en-tête et la grille de cartes tiennent
     déjà leur place, seules les zones de contenu s'allument. */
  if (loading) {
    return (
      <main className="flex-1 p-4 lg:p-6 space-y-6 overflow-y-auto">
        <div className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="h-7 w-40 animate-pulse rounded bg-gray-100" />
              <div className="mt-2 h-4 w-56 max-w-full animate-pulse rounded bg-gray-100" />
            </div>
            <div className="h-4 w-20 animate-pulse rounded bg-gray-100" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="w-full rounded-lg bg-white shadow-md">
                <div className="h-24 animate-pulse rounded-t-lg bg-gray-100" />
                <div className="p-3">
                  <div className="mb-2 h-4 w-3/4 animate-pulse rounded bg-gray-100" />
                  <div className="h-3 w-1/2 animate-pulse rounded bg-gray-100" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 p-4 lg:p-6 space-y-6 overflow-y-auto">
      <div className="bg-white rounded-lg p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Mes Favoris</h2>
            <p className="text-gray-600 mt-1">Vos mobilités préférées</p>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">{favorites.length} favori(s)</span>
          </div>
        </div>

        {favorites.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">💔</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun favori</h3>
            <p className="text-gray-500">Vous n'avez pas encore ajouté de mobilités à vos favoris</p>
            <p className="text-sm text-gray-400 mt-1">Cliquez sur le cœur dans Explorer pour ajouter des favoris</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {favorites.map((vehicle, index) => {
              const icon = getVehicleIcon(vehicle.type || vehicle.vehicle_type);
              // Par défaut, les favoris sont considérés comme disponibles sauf si explicitement marqués comme indisponibles
              const status = vehicle.is_available === false ? 'occupé' : 'disponible';
              const title = vehicle.brand && vehicle.model ? `${vehicle.brand} ${vehicle.model}` : vehicle.serial_number || `Mobilité #${vehicle.id || index + 1}`;
              // Utiliser la même logique de prix que dans Explorer
              const price = vehicle.hourly_rate
                ? `${Number(vehicle.hourly_rate).toFixed(2)}€/h`
                : vehicle.price
                  ? vehicle.price
                  : 'Prix non défini';
              const vehicleId = vehicle.id || vehicle.serial_number || `vehicle-${vehicle.latitude}-${vehicle.longitude}`;

              return (
                <div
                  key={`favorite-${vehicleId}`}
                  className={`w-full rounded-lg shadow-md transition-all duration-300 mobility-card ${removingFavorites[vehicleId]
                      ? 'opacity-0 scale-95 transform'
                      : status === 'disponible'
                        ? 'bg-white hover:shadow-lg cursor-pointer'
                        : 'bg-gray-100 opacity-60 cursor-not-allowed'
                    }`}
                  style={status !== 'disponible' ? { pointerEvents: 'none' } : {}}
                >
                  {/* Image du véhicule */}
                  <div className={`relative h-24 rounded-t-lg flex items-center justify-center ${status === 'disponible'
                      ? 'bg-gradient-to-br from-indigo-100 to-purple-100'
                      : 'bg-gradient-to-br from-gray-200 to-gray-300'
                    }`}>
                    <div className={`text-3xl ${status !== 'disponible' ? 'opacity-50' : ''}`}>
                      {icon}
                    </div>
                    {/* Bouton favori (cœur rouge) */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveFavorite(vehicleId);
                      }}
                      className={`absolute top-2 right-2 w-6 h-6 rounded-full shadow-md flex items-center justify-center transition-colors ${status === 'disponible'
                          ? 'bg-white hover:bg-red-50'
                          : 'bg-gray-300 cursor-not-allowed'
                        }`}
                    >
                      <svg className={`w-4 h-4 ${status === 'disponible' ? 'text-red-500' : 'text-gray-400'} fill-current`} viewBox="0 0 24 24">
                        <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                      </svg>
                    </button>
                  </div>

                  {/* Contenu de la carte */}
                  <div className="p-2">
                    {/* Titre et statut */}
                    <div className="flex items-start justify-between mb-1">
                      <h4 className="font-semibold text-gray-900 text-sm leading-tight flex-1 pr-2">{title}</h4>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${status === 'disponible' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                        {status}
                      </span>
                    </div>

                    {/* Étoiles de notation */}
                    <div className="flex items-center mb-1">
                      <div className="flex space-x-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <svg key={star} className="w-3 h-3 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                        ))}
                      </div>
                      <span className="text-xs text-gray-500 ml-1">4.8 (127)</span>
                    </div>

                    {/* Informations détaillées */}
                    <div className="space-y-0.5 text-xs text-gray-600 mb-2">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center space-x-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <span>{vehicle.latitude && vehicle.longitude ? (cities[`${vehicle.latitude},${vehicle.longitude}`] || 'Chargement...') : 'N/D'}</span>
                        </span>
                        <span className="font-semibold text-indigo-600">{price}</span>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className="text-gray-500">Type: {getVehicleTypeInFrench(vehicle.vehicle_type)}</span>
                        <span className="text-gray-500">ID: {vehicle.serial_number ? vehicle.serial_number.slice(-6) : vehicle.id}</span>
                      </div>
                    </div>

                    {/* Bouton de réservation */}
                    <button
                      onClick={() => {
                        if (status === 'disponible') {
                          // Navigation vers la réservation (même logique que dans Explorer)
                          navigate('/booking', {
                            state: {
                              vehicle: {
                                id: vehicle.id,
                                id_vehicle: vehicle.id, // Ajouter l'ID du véhicule
                                title: title,
                                type: vehicle.type || vehicle.vehicle_type,
                                price: price,
                                distance: vehicle.latitude && vehicle.longitude ? `${Number(vehicle.latitude).toFixed(2)}, ${Number(vehicle.longitude).toFixed(2)}` : 'N/D',
                                status: status,
                                icon: icon,
                                vehicle_type: vehicle.type || vehicle.vehicle_type,
                                hourly_rate: vehicle.hourly_rate ? parseFloat(vehicle.hourly_rate.toString()) : 0,
                                owner_id: vehicle.owner_id || user?.id // Utiliser l'owner_id du véhicule ou l'utilisateur connecté
                              },
                              fromPage: 'favorites' // Indiquer que l'on vient de la page Favoris
                            }
                          });
                        }
                      }}
                      className={`w-full py-1.5 px-4 rounded-lg transition-colors text-sm font-medium ${status === 'disponible'
                          ? 'bg-indigo-600 text-white hover:bg-indigo-700 cursor-pointer'
                          : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}>
                      {status === 'disponible' ? 'Réserver maintenant' : 'Indisponible'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
};

const CommentsView = () => {
  const [selectedConversation, setSelectedConversation] = useState<number | null>(0);
  const [newMessage, setNewMessage] = useState('');
  const [showConversationList, setShowConversationList] = useState(window.innerWidth >= 1024); // true sur desktop, false sur mobile
  const [conversations] = useState([
    {
      id: 0,
      name: 'Support Technique',
      avatar: '🛠️',
      lastMessage: 'Bonjour, comment puis-je vous aider ?',
      time: 'Il y a 2 min',
      unread: 2,
      messages: [
        { id: 1, sender: 'them', text: 'Bonjour, comment puis-je vous aider ?', time: '10:30' },
        { id: 2, sender: 'me', text: 'Bonjour, j\'ai un problème avec ma location', time: '10:32' },
        { id: 3, sender: 'them', text: 'Pouvez-vous me donner plus de détails ?', time: '10:33' },
        { id: 4, sender: 'them', text: 'Quel type de véhicule et quand ?', time: '10:33' }
      ]
    },
    {
      id: 1,
      name: 'Service Client',
      avatar: '👨‍💼',
      lastMessage: 'Votre demande a été traitée',
      time: 'Il y a 1h',
      unread: 0,
      messages: [
        { id: 1, sender: 'them', text: 'Bonjour, votre demande de remboursement a été traitée', time: '09:15' },
        { id: 2, sender: 'me', text: 'Merci beaucoup !', time: '09:20' },
        { id: 3, sender: 'them', text: 'De rien, bonne journée !', time: '09:21' }
      ]
    },
    {
      id: 2,
      name: 'Sécurité',
      avatar: '🔒',
      lastMessage: 'Connexion détectée sur un nouvel appareil',
      time: 'Il y a 3h',
      unread: 1,
      messages: [
        { id: 1, sender: 'them', text: 'Connexion détectée sur un nouvel appareil', time: '07:30' },
        { id: 2, sender: 'them', text: 'Si ce n\'était pas vous, contactez-nous immédiatement', time: '07:30' },
        { id: 3, sender: 'me', text: 'C\'était moi, merci pour l\'alerte', time: '07:35' }
      ]
    },
    {
      id: 3,
      name: 'Promotions',
      avatar: '🎉',
      lastMessage: 'Nouvelle offre : -20% sur les vélos électriques',
      time: 'Hier',
      unread: 0,
      messages: [
        { id: 1, sender: 'them', text: 'Nouvelle offre : -20% sur les vélos électriques', time: 'Hier 14:00' },
        { id: 2, sender: 'me', text: 'Intéressant, je vais regarder', time: 'Hier 14:30' }
      ]
    }
  ]);

  const handleSendMessage = () => {
    if (newMessage.trim() && selectedConversation !== null) {
      const conversation = conversations.find(c => c.id === selectedConversation);
      if (conversation) {
        conversation.messages.push({
          id: conversation.messages.length + 1,
          sender: 'me',
          text: newMessage,
          time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
        });
        conversation.lastMessage = newMessage;
        conversation.time = 'À l\'instant';
        setNewMessage('');
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <main className="flex-1 flex overflow-hidden">
      {/* Liste des conversations */}
      <div className={`${showConversationList ? 'flex' : 'hidden'} lg:flex w-full lg:w-80 bg-white border-r border-gray-200 flex-col`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Conversations</h2>
              <p className="text-sm text-gray-500">4 conversations</p>
            </div>
            {/* Bouton pour fermer la liste sur mobile */}
            <button
              onClick={() => setShowConversationList(false)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Liste */}
        <div className="flex-1 overflow-y-auto">
          {conversations.map((conversation) => (
            <div
              key={conversation.id}
              onClick={() => {
                setSelectedConversation(conversation.id);
                setShowConversationList(false); // Fermer la liste sur mobile
              }}
              className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors ${selectedConversation === conversation.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                }`}
            >
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center text-lg">
                  {conversation.avatar}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {conversation.name}
                    </h3>
                    <span className="text-xs text-gray-500">{conversation.time}</span>
                  </div>
                  <p className="text-sm text-gray-600 truncate mt-1">
                    {conversation.lastMessage}
                  </p>
                </div>
                {conversation.unread > 0 && (
                  <div className="w-5 h-5 bg-blue-500 text-white text-xs rounded-full flex items-center justify-center">
                    {conversation.unread}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Zone de chat */}
      <div className={`${!showConversationList ? 'flex' : 'hidden'} lg:flex flex-1 flex-col bg-gray-50`}>
        {selectedConversation !== null ? (
          <>
            {/* Header du chat */}
            <div className="bg-white border-b border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-lg">
                    {conversations.find(c => c.id === selectedConversation)?.avatar}
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">
                      {conversations.find(c => c.id === selectedConversation)?.name}
                    </h3>
                    <p className="text-xs text-gray-500">En ligne</p>
                  </div>
                </div>
                {/* Bouton pour revenir à la liste sur mobile */}
                <button
                  onClick={() => setShowConversationList(true)}
                  className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                >
                  <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {conversations.find(c => c.id === selectedConversation)?.messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender === 'me' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${message.sender === 'me'
                        ? 'bg-blue-500 text-white'
                        : 'bg-white text-gray-900 border border-gray-200'
                      }`}
                  >
                    <p className="text-sm">{message.text}</p>
                    <p className={`text-xs mt-1 ${message.sender === 'me' ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                      {message.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Zone de saisie */}
            <div className="bg-white border-t border-gray-200 p-4">
              <div className="flex space-x-3">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Tapez votre message..."
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim()}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Envoyer
                </button>
              </div>
            </div>
          </>
        ) : (
          /* État vide */
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="text-gray-400">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune conversation sélectionnée</h3>
              <p className="text-gray-500">Sélectionnez une conversation pour commencer à discuter</p>
              {/* Bouton pour revenir à la liste sur mobile */}
              <button
                onClick={() => setShowConversationList(true)}
                className="lg:hidden mt-4 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
              >
                Voir les conversations
              </button>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

const CalendarView = () => (
  <main className="flex-1 p-4 lg:p-6 space-y-4 lg:space-y-6 overflow-y-auto">
    <div className="bg-white rounded-lg p-6 border border-gray-200">
      <div className="grid grid-cols-7 gap-2">
        {Array.from({ length: 35 }, (_, i) => (
          <div key={i} className="h-12 bg-gray-100 rounded flex items-center justify-center text-sm">
            {i + 1}
          </div>
        ))}
      </div>
    </div>
  </main>
);

const ProjectsView = () => (
  <main className="flex-1 p-4 lg:p-6 space-y-4 lg:space-y-6 overflow-y-auto">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {['Project 1', 'Project 2', 'Project 3'].map((project, index) => (
        <div key={index} className="bg-white rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">{project}</h3>
          <div className="space-y-3">
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            <div className="h-4 bg-gray-300 rounded w-1/2"></div>
          </div>
        </div>
      ))}
    </div>
  </main>
);


interface RentalWithVehicle extends Rental {
  vehicle?: Vehicle;
  // Renseignés par GET /api/admin/rentals (personnel).
  renter?: AdminRentalPerson;
  owner?: AdminRentalPerson;
}

// Les états d'un trajet, comme dans l'app (« À venir » tant que le code de
// remise n'est pas validé, « En cours » ensuite).
type TripGroup = 'request' | 'ongoing' | 'upcoming' | 'completed' | 'cancelled';
const TRIP_GROUPS: { id: TripGroup; label: string }[] = [
  { id: 'request', label: 'Demandes' },
  { id: 'ongoing', label: 'En cours' },
  { id: 'upcoming', label: 'À venir' },
  { id: 'completed', label: 'Terminés' },
  { id: 'cancelled', label: 'Annulés' },
];
const GROUP_META: Record<TripGroup, { label: string; plural: string; tone: Tone }> = {
  request: { label: 'Demande', plural: 'en demande', tone: 'amber' },
  ongoing: { label: 'En cours', plural: 'en cours', tone: 'green' },
  upcoming: { label: 'À venir', plural: 'à venir', tone: 'indigo' },
  completed: { label: 'Terminé', plural: 'terminés', tone: 'grey' },
  cancelled: { label: 'Annulé', plural: 'annulés', tone: 'red' },
};
const EMPTY_TEXT: Record<TripGroup, string> = {
  request: 'Aucune demande en attente',
  ongoing: 'Aucun trajet en cours',
  upcoming: 'Aucun trajet à venir',
  completed: 'Aucun trajet terminé',
  cancelled: 'Aucun trajet annulé',
};
const tripGroup = (r: any): TripGroup => {
  const status = String(r?.status || '');
  if (status === 'pending') return 'request';
  if (status === 'completed') return 'completed';
  if (status === 'cancelled') return 'cancelled';
  return r?.checked ? 'ongoing' : 'upcoming';
};
/** « Rami L. » plutôt que le nom complet, comme dans l'app. */
const shortName = (name?: string | null) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length < 2) return parts[0] || '';
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
};
/** « Mer. 14 oct. 18:00 » ; l'année seulement quand ce n'est pas celle-ci. */
const slotStamp = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const day = d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return `${day.charAt(0).toUpperCase()}${day.slice(1)} ${time}`;
};
/** La ligne sous le nom du véhicule dans le volet. */
const panelDateLine = (r: any) => {
  const group = tripGroup(r);
  const end = r?.end_date ? new Date(r.end_date) : null;
  if (group === 'ongoing' && end) {
    const today = new Date().toDateString() === end.toDateString();
    const time = end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return today ? `Retour aujourd’hui à ${time}` : `Retour ${slotStamp(r.end_date).toLowerCase()}`;
  }
  return `${slotStamp(r?.start_date)} au ${slotStamp(r?.end_date)}`;
};
const photoUrlOf = (url?: string | null) => {
  if (!url) return null;
  if (/^https?:\/\//.test(url)) return url;
  return `${process.env.REACT_APP_API_URL || ''}${String(url).replace(/^\//, '')}`;
};

const TripsView = () => {
  const { user, token, isAuthenticated } = useAuth();
  const isStaff = user?.role === 'admin' || user?.role === 'moderator';
  const [rentals, setRentals] = useState<RentalWithVehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRental, setSelectedRental] = useState<RentalWithVehicle | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [zoneFilter, setZoneFilter] = useState<string>('all');
  const [periodFrom, setPeriodFrom] = useState<string>('');
  const [periodTo, setPeriodTo] = useState<string>('');
  const [usersById, setUsersById] = useState<{ [key: string]: string }>({});
  // Recherche (personnel) : saisie immédiate, requête 300 ms après la dernière
  // frappe. `?q=` (barre « Recherche rapide ») la pré-remplit.
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState<string>(() => searchParams.get('q') || '');
  const [query, setQuery] = useState<string>(() => (searchParams.get('q') || '').trim());
  const [total, setTotal] = useState(0);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const requestRef = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);
  const [rentalReviews, setRentalReviews] = useState<any[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [rentalInspections, setRentalInspections] = useState<any[]>([]);
  const [loadingInspections, setLoadingInspections] = useState(false);
  const [activeGroup, setActiveGroup] = useState<TripGroup>('ongoing');
  const [showHistory, setShowHistory] = useState(false);

  // Récupérer les trajets de l'utilisateur
  useEffect(() => {
    const fetchRentals = async () => {
      /*if (!isAuthenticated) {
        setError('Utilisateur non connecté - Veuillez vous connecter');
        setLoading(false);
        return;
      }

      if (!user?.id) {
        setError('ID utilisateur manquant');
        setLoading(false);
        return;
      }

      if (!token) {
        setError('Token d\'authentification manquant');
        setLoading(false);
        return;
      }*/


      try {
        setLoading(true);
        setError(null);

        console.log('🔍 TripsView - fetchRentals démarré');
        console.log('🔍 TripsView - isAuthenticated:', isAuthenticated);
        console.log('🔍 TripsView - user:', user);
        console.log('🔍 TripsView - token présent:', !!token);

        /*if (!isAuthenticated) {
          console.log('❌ TripsView - Utilisateur non authentifié');
          setError('Utilisateur non connecté - Veuillez vous connecter');
          setLoading(false);
          return;
        }

        if (!user?.id) {
          console.log('❌ TripsView - ID utilisateur manquant');
          setError('ID utilisateur manquant');
          setLoading(false);
          return;
        }

        if (!token) {
          console.log('❌ TripsView - Token manquant');
          setError('Token d\'authentification manquant');
          setLoading(false);
          return;
        }*/

        // Récupérer l'ID utilisateur avec fallback
        const userAny = user as any;
        const userId = (userAny?.id || userAny?.id_user || userAny?.user_id || userAny?.userId)?.toString();

        console.log('🔍 TripsView - user complet:', userAny);
        console.log('🔍 TripsView - userId trouvé:', userId);
        console.log('🔍 TripsView - token raar:', token);

        if (!userId) {
          console.log('❌ TripsView - ID utilisateur manquant');
          setError('ID utilisateur manquant');
          setLoading(false);
          return;
        }

        // Le staff (admin/modérateur) voit tous les trajets, tous utilisateurs
        // confondus ; un utilisateur normal ne voit que les siens (locataire ou
        // propriétaire).
        let allRentals: RentalWithVehicle[] = [];
        if (isStaff) {
          // Une seule requête : trajets + véhicule + locataire + propriétaire,
          // cherchés et filtrés en base (avant : tous les trajets, puis un
          // appel par véhicule, puis tous les comptes pour les noms).
          const requestId = ++requestRef.current;
          const result = await searchAdminRentals(token ?? '', {
            q: query,
            type: typeFilter !== 'all' ? typeFilter : undefined,
            from: periodFrom || undefined,
            to: periodTo || undefined,
            limit: 500
          });
          if (requestId !== requestRef.current) return; // une frappe plus récente a pris le relais
          const names: { [key: string]: string } = {};
          result.rows.forEach((row) => {
            if (row.renter_id) names[row.renter_id] = personName(row.renter) || 'Utilisateur';
            if (row.owner_id) names[row.owner_id] = personName(row.owner) || 'Utilisateur';
          });
          setUsersById((prev) => ({ ...prev, ...names }));
          setTotal(result.total);
          setRentals(result.rows as unknown as RentalWithVehicle[]);
          return;
        } else {
          console.log('🔍 TripsView - Appel getRentalsByRenter...');
          const renterRentals = await getRentalsByRenter(userId, token ?? "");
          console.log('🔍 TripsView - renterRentals:', renterRentals);

          console.log('🔍 TripsView - Appel getRentalsByOwner...');
          const ownerRentals = await getRentalsByOwner(userId, token ?? "");
          console.log('🔍 TripsView - ownerRentals:', ownerRentals);

          allRentals = [...(renterRentals || []), ...(ownerRentals || [])];
        }

        // Dédupliquer par id_rental
        const uniqueRentals = allRentals.filter((rental, index, self) =>
          index === self.findIndex(r => r.id_rental === rental.id_rental)
        );

        console.log('🔍 TripsView - allRentals (avant déduplication):', allRentals);
        console.log('🔍 TripsView - uniqueRentals (après déduplication):', uniqueRentals);

        // Récupérer les informations des véhicules pour chaque trajet
        const rentalsWithVehicles = await Promise.all(
          uniqueRentals.map(async (rental) => {
            try {
              if (rental.vehicle_id) {
                const vehicle = await getVehicleById(rental.vehicle_id, token ?? "");
                return { ...rental, vehicle };
              }
              return rental;
            } catch (error) {
              console.error(`Erreur lors de la récupération du véhicule ${rental.vehicle_id}:`, error);
              return rental;
            }
          })
        );

        setRentals(rentalsWithVehicles);
      } catch (err) {
        console.error('❌ TripsView - Erreur lors de la récupération des trajets:', err);
        setError('Erreur lors de la récupération des trajets');
      } finally {
        setLoading(false);
        setLoadedOnce(true);
      }
    };

    fetchRentals();
    // Les filtres ne relancent la requête que pour le personnel (recherche en base).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, token, isAuthenticated, isStaff, ...(isStaff ? [query, typeFilter, periodFrom, periodTo] : [])]);


  // Convertir les rentals en format d'affichage
  const convertRentalToTrip = (rental: RentalWithVehicle) => {
    const vehicleType = rental.vehicle_type || rental.vehicle?.vehicle_type || '';
    const zoneRaw = (rental as any).lieu_start as string | undefined;
    const zone = zoneRaw && zoneRaw !== 'Localisation non définie' ? zoneRaw : null;

    return {
      id: rental.id_rental || '',
      startDate: rental.start_date || '',
      endDate: rental.end_date || '',
      destination: `#${rental.id_rental?.slice(-8) || 'N/A'}`,
      status: rental.status === 'active' ? 'ongoing' :
        rental.status === 'completed' ? 'completed' :
          rental.status === 'cancelled' ? 'cancelled' :
            rental.status === 'pending' ? 'upcoming' : 'completed',
      vehicleType,
      vehicle: rental.vehicle?.brand && rental.vehicle?.model
        ? `${(VEHICLE_TYPE_LABELS[vehicleType] || vehicleType || 'Type inconnu')} - ${rental.vehicle.brand} ${rental.vehicle.model}`
        : VEHICLE_TYPE_LABELS[vehicleType] || vehicleType || 'Véhicule inconnu',
      zone,
      price: rental.total_price ? `${rental.total_price} TND` : 'Prix non défini',
      renterName: rental.renter ? personName(rental.renter) : (rental.renter_id ? usersById[rental.renter_id] : ''),
      ownerName: rental.owner ? personName(rental.owner) : (rental.owner_id ? usersById[rental.owner_id] : ''),
      rental: rental // Garder la référence au rental original
    };
  };

  // Fonction pour ouvrir la fiche détail d'un trajet
  const openDetails = (trip: any) => {
    setSelectedRental(trip.rental);
    setShowDetails(true);
    setShowHistory(false);
    setRentalReviews([]);
    const rentalId = trip.rental?.id_rental;
    if (rentalId && token) {
      setLoadingReviews(true);
      getAllReviews(token)
        .then((all: any[]) => setRentalReviews((Array.isArray(all) ? all : []).filter((r) => r.rental_id === rentalId)))
        .catch(() => setRentalReviews([]))
        .finally(() => setLoadingReviews(false));

      setRentalInspections([]);
      setLoadingInspections(true);
      getAllInspections(token)
        .then((all: any[]) => setRentalInspections((Array.isArray(all) ? all : []).filter((i) => i.rental_id === rentalId)))
        .catch(() => setRentalInspections([]))
        .finally(() => setLoadingInspections(false));
    }
  };

  // Fonction pour fermer la fiche détail
  const closeDetails = () => {
    setShowDetails(false);
    setSelectedRental(null);
  };

  // Filtrer les trajets par type, zone et période
  const allTrips = rentals.map(convertRentalToTrip);

  const zones = Array.from(new Set(allTrips.map(t => t.zone).filter(Boolean))) as string[];
  // Liste fixe plutôt que dérivée des trajets existants : un type sans trajet
  // pour l'instant propose « 0 résultat » au lieu de disparaître du filtre.
  const types = Object.keys(VEHICLE_TYPE_LABELS).filter((t) => t !== 'autre');

  const trips = allTrips.filter(trip => {
    if (typeFilter !== 'all' && trip.vehicleType !== typeFilter) return false;
    if (zoneFilter !== 'all' && trip.zone !== zoneFilter) return false;
    const tripDate = trip.startDate ? trip.startDate.slice(0, 10) : '';
    if (periodFrom && tripDate && tripDate < periodFrom) return false;
    if (periodTo && tripDate && tripDate > periodTo) return false;
    return true;
  });

  // Les mêmes états que l'app : une demande attend une réponse ; acceptée,
  // la location est à venir tant que le code n'est pas validé, puis en cours.
  const groupOf = (trip: any): TripGroup => tripGroup(trip.rental);
  const counts = TRIP_GROUPS.reduce((acc, g) => ({ ...acc, [g.id]: trips.filter((t) => groupOf(t) === g.id).length }), {} as Record<TripGroup, number>);
  const shown = trips.filter((t) => groupOf(t) === activeGroup);

  // Une recherche qui ne trouve rien dans l'onglet ouvert bascule sur le
  // premier qui a des résultats : « 0 trajet en cours » cachait les autres.
  useEffect(() => {
    if (!query || counts[activeGroup] > 0) return;
    const next = TRIP_GROUPS.find((g) => counts[g.id] > 0);
    if (next) setActiveGroup(next.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, rentals]);

  const [showFilters, setShowFilters] = useState(false);
  const filtersActive = typeFilter !== 'all' || zoneFilter !== 'all' || Boolean(periodFrom || periodTo);
  const [cancelReason, setCancelReason] = useState('');
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const cancelSelected = async () => {
    if (!selectedRental?.id_rental || !token || !cancelReason.trim()) return;
    setBusy(true);
    try {
      await cancelRental(selectedRental.id_rental, cancelReason.trim(), token);
      setRentals((prev) => prev.map((r) => (r.id_rental === selectedRental.id_rental ? { ...r, status: 'cancelled', cancellation_reason: cancelReason.trim() } as any : r)));
      setSelectedRental((r) => (r ? ({ ...r, status: 'cancelled', cancellation_reason: cancelReason.trim() } as any) : r));
      setConfirmCancel(false);
      setCancelReason('');
      setActionMessage('Location annulée, les deux parties sont prévenues.');
    } catch (e: any) {
      setActionMessage(e?.message || 'Annulation impossible.');
    } finally {
      setBusy(false);
    }
  };

  const [messageOpen, setMessageOpen] = useState(false);
  const [messageText, setMessageText] = useState('');
  const writeToBoth = async () => {
    if (!selectedRental || !token || !messageText.trim()) return;
    setBusy(true);
    try {
      const ids = [selectedRental.renter_id, selectedRental.owner_id].filter(Boolean) as string[];
      await Promise.all(ids.map((id) => sendCustomNotification({ title: 'Message de l’équipe Dingo', body: messageText.trim(), target: 'user', userId: id }, token)));
      setMessageOpen(false);
      setMessageText('');
      setActionMessage('Message envoyé aux deux parties.');
    } catch (e: any) {
      setActionMessage(e?.message || 'Envoi impossible.');
    } finally {
      setBusy(false);
    }
  };

  const TABLE_COLS = 'lg:grid-cols-[1.85fr_1.15fr_1.5fr_1.65fr_0.8fr_0.7fr_16px]';
  const Skel = ({ className }: { className: string }) => <div className={`${className} animate-pulse rounded-lg bg-[#EEF0F5]`} />;

  const header = (
    <div className={`hidden gap-3 px-5 pb-2.5 pt-3 lg:grid ${TABLE_COLS}`}>
      {['Véhicule', 'Locataire', 'Propriétaire', 'Créneau', 'État'].map((h) => <SectionLabel key={h}>{h}</SectionLabel>)}
      <SectionLabel className="text-right">Montant</SectionLabel><span />
    </div>
  );

  if (error) {
    return (
      <main className="flex-1 overflow-y-auto bg-[#F7F7FB] px-4 pb-6 pt-1 lg:px-7">
        <Card className="px-6 py-10 text-center">
          <p className="text-[17px] font-extrabold text-[#1E293B]">Les trajets ne se chargent pas</p>
          <p className="mt-1.5 text-[13.5px] text-[#6B7280]">Le service ne répond pas pour l’instant. Réessayez dans un moment.</p>
        </Card>
      </main>
    );
  }

  const selectedKey = selectedRental?.id_rental;
  const sel = selectedRental as any;

  return (
    <main className="flex min-h-0 flex-1 gap-4 overflow-hidden bg-[#F7F7FB] px-4 pb-6 pt-1 lg:px-7">
      <div className="flex min-w-0 flex-1 flex-col gap-3.5 overflow-y-auto [&>*]:shrink-0">
        {/* États, puis les filtres et la recherche */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex flex-wrap gap-1.5">
            {TRIP_GROUPS.map((g) => (
              <FilterChip key={g.id} active={activeGroup === g.id} count={loadedOnce ? counts[g.id] : undefined}
                countTone={g.id === 'request' && counts.request > 0 ? 'amber' : undefined}
                onClick={() => setActiveGroup(g.id)}>
                {g.label}
              </FilterChip>
            ))}
          </div>
          <span className="flex-1" />
          {isStaff && (
            <div className="relative w-full sm:w-[220px]">
              <LineIcon d={ICONS.search} size={15} color="#9CA3AF" className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input type="search" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Nom, téléphone, véhicule, n°…"
                title="Nom ou prénom, e-mail, téléphone, marque ou modèle, numéro de série, numéro de trajet (#xxxxxxxx) ; plusieurs mots se combinent"
                aria-label="Chercher un trajet"
                className="h-8 w-full rounded-full border border-[#E5E7EB] bg-white pl-9 pr-3 text-[12.5px] focus:border-[#C7C4F6] focus:outline-none focus:ring-4 focus:ring-[#4F46E5]/10" />
            </div>
          )}
          <FilterChip active={filtersActive} onClick={() => setShowFilters((v) => !v)}
            icon={<LineIcon d={ICONS.filter} size={14} color={filtersActive ? '#fff' : '#4B5563'} />}>
            Filtrer
          </FilterChip>
        </div>

        {showFilters && (
          <Card className="flex flex-wrap items-end gap-4 px-5 py-4">
            <label className="flex flex-col gap-1">
              <SectionLabel>Type</SectionLabel>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
                className="h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#4F46E5]">
                <option value="all">Tous</option>
                {types.map((t) => <option key={t} value={t}>{VEHICLE_TYPE_LABELS[t] || t}</option>)}
              </select>
            </label>
            {zones.length > 0 && (
              <label className="flex flex-col gap-1">
                <SectionLabel>Zone</SectionLabel>
                <select value={zoneFilter} onChange={(e) => setZoneFilter(e.target.value)}
                  className="h-10 rounded-xl border border-[#E5E7EB] bg-white px-3 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#4F46E5]">
                  <option value="all">Toutes</option>
                  {zones.map((z) => <option key={z} value={z}>{z}</option>)}
                </select>
              </label>
            )}
            <label className="flex flex-col gap-1">
              <SectionLabel>Du</SectionLabel>
              <input type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)}
                className="h-10 rounded-xl border border-[#E5E7EB] px-3 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#4F46E5]" />
            </label>
            <label className="flex flex-col gap-1">
              <SectionLabel>Au</SectionLabel>
              <input type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)}
                className="h-10 rounded-xl border border-[#E5E7EB] px-3 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#4F46E5]" />
            </label>
            {filtersActive && (
              <LinkButton onClick={() => { setTypeFilter('all'); setZoneFilter('all'); setPeriodFrom(''); setPeriodTo(''); }}>Tout effacer</LinkButton>
            )}
          </Card>
        )}

        <Card className={`overflow-hidden transition-opacity ${loading && loadedOnce ? 'opacity-60' : ''}`}>
          {header}
          {!loadedOnce ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 border-t border-[#EEF0F4] px-5 py-3">
                <Skel className="h-11 w-11 rounded-xl" /><div className="flex-1 space-y-2"><Skel className="h-3.5 w-40" /><Skel className="h-3 w-24" /></div><Skel className="h-4 w-16" />
              </div>
            ))
          ) : shown.length === 0 ? (
            <div className="border-t border-[#EEF0F4] px-6 py-12 text-center">
              <p className="text-[15px] font-bold text-[#1E293B]">{EMPTY_TEXT[activeGroup]}</p>
              <p className="mt-1 text-[13px] text-[#6B7280]">{query ? `Aucun résultat pour « ${query} » dans cet onglet.` : 'Ils apparaîtront ici dès qu’il y en aura.'}</p>
            </div>
          ) : (
            shown.map((trip: any, index: number) => {
              const r = trip.rental as any;
              const meta = GROUP_META[groupOf(trip)];
              const active = r.id_rental === selectedKey;
              const name = [r.vehicle?.brand, r.vehicle?.model].filter(Boolean).join(' ') || VEHICLE_TYPE_LABELS[trip.vehicleType] || 'Véhicule';
              return (
                <button key={`${trip.id}-${index}`} type="button" onClick={() => openDetails(trip)}
                  className={`grid w-full grid-cols-[1fr_auto] items-center gap-3 border-t border-[#EEF0F4] px-5 py-3 text-left transition-colors ${TABLE_COLS} ${
                    active ? 'bg-[#F7F6FF] shadow-[inset_3px_0_0_#4F46E5]' : 'hover:bg-[#FBFBFE]'
                  }`}>
                  <span className="flex min-w-0 items-center gap-3">
                    <VehicleThumb vehicle={r.vehicle} type={trip.vehicleType} />
                    <span className="min-w-0">
                      <span className="block truncate text-[14px] font-bold text-[#1E293B]">{name}</span>
                      <span className="block truncate text-[12px] text-[#6B7280]">{r.vehicle?.serial_number || 'Sans plaque'}</span>
                    </span>
                  </span>
                  <span className="hidden min-w-0 items-center gap-2 lg:flex">
                    <Avatar name={trip.renterName} photo={photoUrlOf(r.renter?.profile_photo)} size={28} />
                    <span className="truncate text-[13px]">{shortName(trip.renterName) || '—'}</span>
                  </span>
                  <span className="hidden min-w-0 items-center gap-2 lg:flex">
                    <Avatar name={trip.ownerName} photo={photoUrlOf(r.owner?.profile_photo)} size={28} tone="orange" />
                    <span className="truncate text-[13px]">{shortName(trip.ownerName) || '—'}</span>
                  </span>
                  <span className="hidden whitespace-nowrap text-[12.5px] leading-[1.4] text-[#4B5563] lg:block">
                    {slotStamp(trip.startDate)}<br /><span className="text-[#9CA3AF]">au</span> {slotStamp(trip.endDate)}
                  </span>
                  <span className="hidden lg:block"><StatusPill tone={meta.tone}>{meta.label}</StatusPill></span>
                  <b className="whitespace-nowrap text-right text-[14px] font-extrabold text-[#1E293B]">{r.total_price ? formatDT(r.total_price) : '—'}</b>
                  <span className="hidden lg:block"><LineIcon d={ICONS.chevron} size={16} color="#9CA3AF" width={2.4} /></span>
                </button>
              );
            })
          )}
          {loadedOnce && (
            <div className="border-t border-[#EEF0F4] px-5 py-3.5 text-[12.5px] text-[#6B7280]" aria-live="polite">
              {shown.length} {shown.length > 1 ? 'trajets' : 'trajet'} {GROUP_META[activeGroup].plural}
              {isStaff && rentals.length < total && `, les ${rentals.length} plus récents sur ${total.toLocaleString('fr-FR')} ; précisez la recherche pour les autres`}
            </div>
          )}
        </Card>
      </div>

      {/* Volet de la location choisie */}
      {showDetails && selectedRental && (() => {
        const vType = selectedRental.vehicle_type || selectedRental.vehicle?.vehicle_type || '';
        const name = [sel.vehicle?.brand, sel.vehicle?.model].filter(Boolean).join(' ') || VEHICLE_TYPE_LABELS[vType] || 'Véhicule';
        const meta = GROUP_META[tripGroup(sel)];
        const tripPickup = positionOrNull(selectedRental.vehicle?.latitude, selectedRental.vehicle?.longitude);
        const tripDropoff = positionOrNull((selectedRental.vehicle as any)?.return_latitude, (selectedRental.vehicle as any)?.return_longitude);
        const price = Number(sel.total_price || 0);
        const commission = Number(sel.owner_commission || 0);
        const serviceFee = Number(sel.service_fee || 0) + Number(sel.protection_fee || 0);
        const share = Number(sel.owner_fee_share || 0);
        const ownerNet = price - commission + share;
        const days = sel.start_date && sel.end_date ? Math.max(1, Math.ceil((new Date(sel.end_date).getTime() - new Date(sel.start_date).getTime()) / 86400000)) : null;
        const canCancel = isStaff && ['pending', 'active', 'confirmed'].includes(String(sel.status));
        const person = (title: string, p: any, fallback: string | null, tone: 'indigo' | 'orange', extra: string | null) => (
          <div className="flex items-center gap-3 py-[11px]" style={{ borderTop: title === 'Propriétaire' ? '1px solid #EEF0F4' : 'none' }}>
            <Avatar name={personName(p) || fallback} photo={photoUrlOf(p?.profile_photo)} size={40} tone={tone} />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] font-bold text-[#1E293B]">{personName(p) || fallback || '—'}</span>
              <span className="flex items-center gap-1 truncate text-[12px] text-[#6B7280]">
                {title}
                {p?.average_rating && Number(p.average_rating) > 0 ? <> · <span className="text-[#F59E0B]">★</span> {Number(p.average_rating).toFixed(1)}</> : null}
                {extra ? <> · {extra}</> : null}
              </span>
            </span>
            {p?.phone && (
              <a href={`tel:${String(p.phone).replace(/\s/g, '')}`} title={p.phone} aria-label={`Appeler ${personName(p)}`}
                className="flex h-[34px] w-[34px] items-center justify-center rounded-full border border-[#E5E7EB] bg-white hover:bg-[#F7F7FB]">
                <LineIcon d={ICONS.phone} size={15} color="#4F46E5" />
              </a>
            )}
          </div>
        );
        return (
          <aside className="hidden w-[380px] shrink-0 flex-col overflow-hidden rounded-[20px] border border-[#EEF0F4] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)] lg:flex">
            <div className="min-h-0 flex-1 overflow-y-auto">
              <div className="flex items-center justify-between px-[22px] pt-4">
                <SectionLabel>Location #{sel.id_rental?.slice(-4)}</SectionLabel>
                <CloseButton onClick={closeDetails} />
              </div>
              <div className="flex items-center gap-4 px-[22px] pb-[18px] pt-3.5">
                <VehicleThumb vehicle={sel.vehicle} type={vType} size={88} radius={14} />
                <div className="min-w-0">
                  <b className="block truncate text-[17px] font-extrabold text-[#1E293B]">{name}</b>
                  <span className="block text-[12px] text-[#6B7280]">{panelDateLine(sel)}</span>
                  <div className="mt-[7px] flex flex-wrap gap-1.5">
                    <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                    {sel.vehicle?.serial_number && (
                      <span className="rounded-md border-[1.5px] border-[#CBD5E1] px-1.5 text-[11px] font-bold leading-[16px] text-[#1E293B]">{sel.vehicle.serial_number}</span>
                    )}
                  </div>
                </div>
              </div>

              {actionMessage && (
                <div className="mx-[22px] mb-3 rounded-xl bg-[#ECFDF3] px-3 py-2 text-[12.5px] font-semibold text-[#047857]">{actionMessage}</div>
              )}

              <Band />
              <div className="px-[22px] pb-1 pt-3.5"><SectionLabel>Personnes</SectionLabel></div>
              <div className="px-[22px]">
                {person('Locataire', sel.renter, sel.renter_id ? usersById[sel.renter_id] : null, 'indigo', sel.renter?.license_verified ? 'permis vérifié' : null)}
                {person('Propriétaire', sel.owner, sel.owner_id ? usersById[sel.owner_id] : null, 'orange', null)}
              </div>

              <Band className="mt-2" />
              <div className="px-[22px] pb-1 pt-3.5"><SectionLabel>Argent</SectionLabel></div>
              <div className="px-[22px] text-[13px]">
                {[
                  [`Location${days ? `, ${days} ${days > 1 ? 'jours' : 'jour'}` : ''}`, price ? formatDT(price) : '—', ''],
                  ['Frais de service', serviceFee > 0 ? formatDT(serviceFee) : 'Offerts', serviceFee > 0 ? '' : 'text-[#047857]'],
                  ['Commission Dingo', commission > 0 ? `−${formatDT(commission)}` : 'Offerte', commission > 0 ? 'text-[#6B7280]' : 'text-[#047857]'],
                  ['Le propriétaire touche', price ? formatDT(ownerNet) : '—', 'text-[15px] font-extrabold'],
                ].map(([label, value, cls], i) => (
                  <div key={label} className="flex py-[9px]" style={{ borderTop: i ? '1px solid #EEF0F4' : 'none' }}>
                    <span className="flex-1 text-[#4B5563]">{label}</span><b className={`font-bold ${cls}`}>{value}</b>
                  </div>
                ))}
              </div>

              {tripPickup && (
                <>
                  <Band className="mt-2" />
                  <div className="px-[22px] pb-2 pt-3.5"><SectionLabel>Itinéraire</SectionLabel></div>
                  <div className="px-[22px] pb-2"><RouteMap pickup={tripPickup} dropoff={tripDropoff} vehicleType={vType} /></div>
                </>
              )}

              {sel.status === 'cancelled' && sel.cancellation_reason && (
                <div className="mx-[22px] my-3 rounded-xl bg-[#FDECEF] px-3 py-2.5">
                  <SectionLabel className="!text-[#B91C1C]">Motif d’annulation</SectionLabel>
                  <p className="mt-1 text-[13px] text-[#B91C1C]">{sel.cancellation_reason}</p>
                </div>
              )}

              <Band className="mt-2" />
              <div className="px-[22px] pb-1 pt-3.5"><SectionLabel>Actions</SectionLabel></div>
              <ActionRow first icon="journal" tone="grey" title="Historique de la location" subtitle="États des lieux et avis"
                onClick={() => setShowHistory((v) => !v)} />
              {showHistory && (
                <div className="space-y-2 px-[22px] pb-2">
                  {loadingInspections || loadingReviews ? (
                    <Skel className="h-14 w-full" />
                  ) : rentalInspections.length === 0 && rentalReviews.length === 0 ? (
                    <p className="py-2 text-[12.5px] text-[#6B7280]">Aucun état des lieux ni avis pour cette location.</p>
                  ) : (
                    <>
                      {rentalInspections.map((insp) => {
                        const photos = [insp.photo1_url, insp.photo2_url, insp.photo3_url, insp.photo4_url, insp.photo5_url].filter(Boolean);
                        const label = insp.type === 'pre_rental' ? 'État des lieux, départ' : insp.type === 'post_rental' ? 'État des lieux, retour' : insp.type === 'damage' ? 'Dégât signalé' : 'Maintenance';
                        return (
                          <div key={insp.id_inspection} className="rounded-xl bg-[#F7F7FB] p-2.5">
                            <div className="flex items-center justify-between"><b className="text-[12.5px]">{label}</b><span className="text-[11px] text-[#9CA3AF]">{insp.inspection_date ? new Date(insp.inspection_date).toLocaleDateString('fr-FR') : ''}</span></div>
                            {insp.comment && <p className="mt-1 text-[12px] text-[#4B5563]">{insp.comment}</p>}
                            {photos.length > 0 && (
                              <div className="mt-1.5 flex flex-wrap gap-1.5">
                                {photos.map((url: string, i: number) => (
                                  <a key={i} href={url} target="_blank" rel="noreferrer" className="h-11 w-11 overflow-hidden rounded-lg"><img src={url} alt="" className="h-full w-full object-cover" /></a>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {rentalReviews.map((r) => (
                        <div key={r.id_review} className="rounded-xl bg-[#F7F7FB] p-2.5">
                          <span className="text-[12px] text-[#F59E0B]">{'★'.repeat(Math.max(0, Math.min(5, Math.round(r.rating))))}<span className="text-[#E5E7EB]">{'★'.repeat(5 - Math.max(0, Math.min(5, Math.round(r.rating))))}</span></span>
                          {r.comment && <p className="text-[12px] text-[#4B5563]">{r.comment}</p>}
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
              {isStaff && (
                <ActionRow icon="message" title="Écrire aux deux parties" subtitle="Une notification à chacun dans l’app"
                  onClick={() => setMessageOpen((v) => !v)} />
              )}
              {messageOpen && (
                <div className="space-y-2 px-[22px] pb-3">
                  <textarea value={messageText} onChange={(e) => setMessageText(e.target.value)} rows={3}
                    placeholder="Votre message au locataire et au propriétaire"
                    className="w-full rounded-xl border border-[#E5E7EB] px-3 py-2 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#4F46E5]" />
                  <BigButton className="h-10 w-full text-[14px]" disabled={busy || !messageText.trim()} onClick={writeToBoth}>Envoyer</BigButton>
                </div>
              )}
              {canCancel && (
                <ActionRow icon="close" tone="red" title="Annuler la location" subtitle="Les deux parties sont prévenues"
                  onClick={() => setConfirmCancel((v) => !v)} />
              )}
              {confirmCancel && (
                <div className="space-y-2 px-[22px] pb-4">
                  <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Motif, envoyé aux deux parties"
                    className="h-10 w-full rounded-xl border border-[#E5E7EB] px-3 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/40" />
                  <BigButton kind="danger" className="h-10 w-full text-[14px]" disabled={busy || !cancelReason.trim()} onClick={cancelSelected}>Annuler la location</BigButton>
                </div>
              )}
              <div className="h-4" />
            </div>
          </aside>
        );
      })()}
    </main>
  );
};

const StarredView = () => (
  <main className="flex-1 p-4 lg:p-6 space-y-4 lg:space-y-6 overflow-y-auto">
    <div className="space-y-4">
      {[1, 2, 3, 4].map((item) => (
        <div key={item} className="bg-white rounded-lg p-6 border border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
              <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
              </svg>
            </div>
            <div className="flex-1">
              <div className="h-4 bg-gray-300 rounded w-1/3 mb-2"></div>
              <div className="h-3 bg-gray-300 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  </main>
);

/*const MyRentalsView = () => {
  return <FleetView />;
};

  const { token, user } = useAuth();
  const [userVehicles, setUserVehicles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [cities, setCities] = useState<{[key: string]: string}>({});
  const [showAddVehicle, setShowAddVehicle] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState<any>(null);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [vehicleForm, setVehicleForm] = useState({
    serial_number: '',
    vehicle_type: 'car',
    brand: '',
    model: '',
    year: '',
    is_platform_owned: false,
    is_available: true,
    hourly_rate: '',
    latitude: '',
    longitude: ''
  });

  // Statistiques mockées pour l'instant
  const [stats, setStats] = useState({
    totalVehicles: 0,
    totalRevenue: 0,
    totalTrips: 0,
    totalKilometers: 0
  });

  // Fonction pour charger les villes des véhicules
  const loadCities = async (vehicles: any[]) => {
    console.log('🏙️ Chargement des villes pour', vehicles.length, 'véhicules');
    for (const vehicle of vehicles) {
      console.log('🚗 Véhicule:', { 
        id: vehicle.id_vehicle || vehicle.id, 
        lat: vehicle.latitude, 
        lng: vehicle.longitude,
        type: typeof vehicle.latitude,
        lngType: typeof vehicle.longitude
      });
      
      if (vehicle.latitude && vehicle.longitude) {
        const cityKey = `${vehicle.latitude},${vehicle.longitude}`;
        console.log('🔑 Clé de ville:', cityKey);
        
        if (!cities[cityKey]) {
          console.log('🔄 Chargement de la ville pour:', cityKey);
          try {
            const city = await getCityFromCoords(vehicle.latitude, vehicle.longitude);
            console.log('✅ Ville chargée:', city);
            // Mettre à jour immédiatement chaque ville trouvée
            setCities(prev => ({ ...prev, [cityKey]: city }));
            // Délai de 500ms entre les requêtes pour respecter le rate limiting
            await new Promise(resolve => setTimeout(resolve, 500));
          } catch (error) {
            console.error('❌ Erreur lors du chargement de la ville:', error);
            setCities(prev => ({ ...prev, [cityKey]: 'Ville inconnue' }));
          }
        } else {
          console.log('💾 Ville déjà en cache:', cities[cityKey]);
        }
      } else {
        console.log('⚠️ Coordonnées manquantes pour le véhicule:', vehicle.id_vehicle || vehicle.id);
      }
    }
  };

  // Charger les villes quand les véhicules changent (en arrière-plan)
  useEffect(() => {
    if (userVehicles.length > 0) {
      // Charger les villes en arrière-plan sans bloquer l'interface
      loadCities(userVehicles).catch(console.error);
    }
  }, [userVehicles]);

  // Fonction de test pour ajouter un véhicule avec des coordonnées valides
  const addTestVehicle = () => {
    const testVehicle = {
      id_vehicle: 'test-1',
      serial_number: 'TEST001',
      vehicle_type: 'car',
      brand: 'Test',
      model: 'Vehicle',
      hourly_rate: 15,
      is_available: true,
      latitude: 48.8566,
      longitude: 2.3522
    };
    setUserVehicles(prev => [...prev, testVehicle]);
  };

  // Charger les véhicules de l'utilisateur
  useEffect(() => {
    console.log('🔄 useEffect DiscoverView déclenché - Token:', !!token, 'User:', !!user);
    
    const loadUserVehicles = async () => {
      console.log('🔍 Debug loadUserVehicles - Token rami:', !!token, 'User:', user);
      console.log('🔍 Debug loadUserVehicles - Loading state:', loading);
      
      if (!token || !user?.id) {
        console.log('❌ Token ou user manquant');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        console.log('📡 Chargement des véhicules pour l\'utilisateur:', user?.id);
        
        // Récupérer directement les véhicules du propriétaire
        console.log('📡 Appel API getVehiclesByOwner...');
        const userAny = user as any;
        const userId = userAny?.id || userAny?.id_user || userAny?.user_id || userAny?.userId;
        
        console.log('🆔 User ID utilisé:', userId);
        
        const userVehicles = await getVehiclesByOwner(userId.toString(), token);
        console.log('✅ Véhicules récupérés:', userVehicles);
        
        if (Array.isArray(userVehicles)) {
          setUserVehicles(userVehicles);
          console.log('📊 Nombre de véhicules chargés:', userVehicles.length);
        } else {
          console.log('⚠️ Réponse non-array:', userVehicles);
          setUserVehicles([]);
        }
      } catch (error) {
        console.error('❌ Erreur lors du chargement des véhicules:', error);
        setUserVehicles([]);
      } finally {
        setLoading(false);
      }
    };

    console.log('🚀 Appel de loadUserVehicles()');
    loadUserVehicles();
  }, [token, user]);
            


  // Fonction pour obtenir l'icône du véhicule
  const getVehicleIcon = (vehicleType: string) => {
    switch (vehicleType) {
      case 'car': return '🚗';
      case 'motorcycle': return '🏍️';
      case 'scooter': return '🛴';
      case 'bike': return '🚲';
      case 'quad': return '🏎️';
      default: return '🚗';
    }
  };

  // Fonction pour obtenir le type de véhicule en français
  const getVehicleTypeInFrench = (vehicleType: string) => {
    switch (vehicleType) {
      case 'car': return 'Voiture';
      case 'motorcycle': return 'Moto';
      case 'scooter': return 'Scooter';
      case 'bike': return 'Vélo';
      case 'quad': return 'Quad';
      default: return 'Véhicule';
    }
  };

  // Fonction pour ouvrir le formulaire d'ajout
  const openAddVehicle = () => {
    setVehicleForm({
      serial_number: '',
      vehicle_type: 'car',
      brand: '',
      model: '',
      year: '',
      is_platform_owned: false,
      is_available: true,
      hourly_rate: '',
      latitude: '',
      longitude: ''
    });
    setEditingVehicle(null);
    setShowAddVehicle(true);
  };

  // Fonction pour ajouter un véhicule avec les mêmes méthodes que UserDetailsView
  const addVehicle = async () => {
    console.log('🔍 Debug - Token:', !!token, 'User:', user);
    console.log('🔍 Debug - User complet:', JSON.stringify(user, null, 2));
    
    // Essayer différents champs d'ID possibles
    const userAny = user as any;
    const userId = userAny?.id || userAny?.id_user || userAny?.user_id || userAny?.userId;
    console.log('🔍 ID utilisateur trouvé:', userId, 'dans:', user);
    
    if (!token || !userId) {
      console.error('Token ou utilisateur manquant - Token:', !!token, 'User ID:', userId);
      console.error('User complet:', user);
      return;
    }

    try {
      const vehicleData = {
        serial_number: vehicleForm.serial_number,
        vehicle_type: vehicleForm.vehicle_type,
        brand: vehicleForm.brand,
        model: vehicleForm.model,
        year: vehicleForm.year,
        is_platform_owned: vehicleForm.is_platform_owned,
        is_available: vehicleForm.is_available,
        hourly_rate: parseFloat(vehicleForm.hourly_rate) || 0,
        latitude: vehicleForm.latitude ? parseFloat(vehicleForm.latitude) : null,
        longitude: vehicleForm.longitude ? parseFloat(vehicleForm.longitude) : null,
        owner_id: userId.toString()
      };
      
      console.log('📡 Ajout du véhicule avec owner_id:', vehicleData.owner_id);

      console.log('📡 Ajout du véhicule:', vehicleData);

      const response = await fetch(`${process.env.REACT_APP_API_URL}api/vehicles`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(vehicleData)
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const newVehicle = await response.json();
      console.log('✅ Véhicule ajouté avec succès:', newVehicle);

      // Ajouter le nouveau véhicule à la liste
      setUserVehicles(prev => [...prev, newVehicle]);
      setStats(prev => ({ ...prev, totalVehicles: prev.totalVehicles + 1 }));

      // Fermer le modal
      closeVehicleForm();

      // Afficher un message de succès (vous pouvez ajouter un toast ici)
      console.log('🎉 Véhicule ajouté avec succès !');

    } catch (error: any) {
      console.error('❌ Erreur lors de l\'ajout du véhicule:', error);
      // Afficher un message d'erreur (vous pouvez ajouter un toast ici)
      console.error('💥 Erreur:', error.message);
    }
  };

  // Fonction pour ouvrir le formulaire de modification
  const openEditVehicle = (vehicle: any) => {
    setVehicleForm({
      serial_number: vehicle.serial_number || '',
      vehicle_type: vehicle.vehicle_type || 'car',
      brand: vehicle.brand || '',
      model: vehicle.model || '',
      year: vehicle.year || '',
      is_platform_owned: vehicle.is_platform_owned || false,
      is_available: vehicle.is_available === true,
      hourly_rate: vehicle.hourly_rate || '',
      latitude: vehicle.latitude || '',
      longitude: vehicle.longitude || ''
    });
    setEditingVehicle(vehicle);
    setShowAddVehicle(true);
  };

  // Fonction pour fermer le formulaire
  const closeVehicleForm = () => {
    setShowAddVehicle(false);
    setEditingVehicle(null);
    setShowLocationPicker(false);
    setVehicleForm({
      serial_number: '',
      vehicle_type: 'car',
      brand: '',
      model: '',
      year: '',
      is_platform_owned: false,
      is_available: true,
      hourly_rate: '',
      latitude: '',
      longitude: ''
    });
  };

  // Fonction pour gérer la sélection de position
  const handleLocationSelect = (lat: number, lng: number) => {
    setVehicleForm(prev => ({
      ...prev,
      latitude: lat.toString(),
      longitude: lng.toString()
    }));
  };

  // Fonction pour sauvegarder un véhicule
  const saveVehicle = async () => {
    console.log('🚀 saveVehicle appelée !');
    console.log('🔍 Debug saveVehicle - Token:', !!token, 'User:', user);
    console.log('🔍 editingVehicle:', editingVehicle);
    console.log('🔍 vehicleForm:', vehicleForm);
    
    if (!token || !user?.id) {
      console.log('❌ Token ou user manquant pour sauvegarder le véhicule');
      return;
    }
    
    try {
      const userAny = user as any;
      const userId = userAny?.id || userAny?.id_user || userAny?.user_id || userAny?.userId;
      
      const vehicleData = {
        serial_number: vehicleForm.serial_number,
        vehicle_type: vehicleForm.vehicle_type,
        brand: vehicleForm.brand,
        model: vehicleForm.model,
        year: vehicleForm.year,
        is_platform_owned: vehicleForm.is_platform_owned,
        is_available: vehicleForm.is_available,
        hourly_rate: parseFloat(vehicleForm.hourly_rate) || 0,
        latitude: vehicleForm.latitude ? parseFloat(vehicleForm.latitude) : null,
        longitude: vehicleForm.longitude ? parseFloat(vehicleForm.longitude) : null,
        owner_id: userId.toString()
      };

      if (editingVehicle) {
        // Modifier un véhicule existant
        const response = await fetch(`${process.env.REACT_APP_API_URL}api/vehicles/${editingVehicle.id_vehicle}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(vehicleData)
        });

        if (response.ok) {
          const updatedVehicle = await response.json();
          setUserVehicles(prev => prev.map(v => v.id_vehicle === editingVehicle.id_vehicle ? updatedVehicle : v));
        }
      } else {
        // Ajouter un nouveau véhicule
        const response = await fetch(`${process.env.REACT_APP_API_URL}api/vehicles`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(vehicleData)
        });

        if (response.ok) {
          const newVehicle = await response.json();
          setUserVehicles(prev => [...prev, newVehicle]);
          setStats(prev => ({ ...prev, totalVehicles: prev.totalVehicles + 1 }));
        }
      }
      
      closeVehicleForm();
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
    }
  };

  // Fonction pour supprimer un véhicule
  const deleteVehicle = async (vehicleId: string) => {
    if (!token) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/vehicles/${vehicleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setUserVehicles(prev => prev.filter(v => v.id_vehicle !== vehicleId));
        setStats(prev => ({ ...prev, totalVehicles: prev.totalVehicles - 1 }));
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
    }
  };

  return (
    <main className="flex-1 overflow-y-auto p-3 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
        {/* Cartes de statistiques 
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-6">
            <div className="flex items-center">
              <div className="p-1.5 sm:p-2 bg-purple-100 rounded-lg">
                <svg className="w-4 h-4 sm:w-6 sm:h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                </svg>
              </div>
              <div className="ml-2 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Ma Flotte</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.totalVehicles}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-6">
            <div className="flex items-center">
              <div className="p-1.5 sm:p-2 bg-green-100 rounded-lg">
                <svg className="w-4 h-4 sm:w-6 sm:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"/>
                </svg>
              </div>
              <div className="ml-2 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Revenus</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.totalRevenue.toFixed(2)}€</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-6">
            <div className="flex items-center">
              <div className="p-1.5 sm:p-2 bg-blue-100 rounded-lg">
                <svg className="w-4 h-4 sm:w-6 sm:h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
              </div>
              <div className="ml-2 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Trajets</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.totalTrips}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-6">
            <div className="flex items-center">
              <div className="p-1.5 sm:p-2 bg-orange-100 rounded-lg">
                <svg className="w-4 h-4 sm:w-6 sm:h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-1.447-.894L15 4m0 13V4m-6 3l6-3"/>
                </svg>
              </div>
              <div className="ml-2 sm:ml-4">
                <p className="text-xs sm:text-sm font-medium text-gray-600">Kilomètres</p>
                <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.totalKilometers}km</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section des véhicules 
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 sm:p-6 border-b border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Mes Véhicules</h2>
                <p className="text-xs sm:text-sm text-gray-600">Gérez votre flotte de mobilités</p>
              </div>
              <button
                onClick={openAddVehicle}
                className="bg-purple-600 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2 w-full sm:w-auto"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                </svg>
                <span className="hidden sm:inline">Ajouter</span>
                <span className="sm:hidden">Ajouter un véhicule</span>
              </button>
            </div>

        

              {/* Modal pour ajouter/modifier un véhicule 
              {showAddVehicle && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      {editingVehicle ? 'Modifier le véhicule' : 'Ajouter un véhicule'}
                    </h3>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de série</label>
                        <input
                          type="text"
                          value={vehicleForm.serial_number}
                          onChange={(e) => setVehicleForm({...vehicleForm, serial_number: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                          placeholder="Numéro de série"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Type de véhicule</label>
                        <select
                          value={vehicleForm.vehicle_type}
                          onChange={(e) => setVehicleForm({...vehicleForm, vehicle_type: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="car">Voiture</option>
                          <option value="motorcycle">Moto</option>
                          <option value="scooter">Scooter</option>
                          <option value="bike">Vélo</option>
                  <option value="quad">Quad</option>
                        </select>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Marque</label>
                          <input
                            type="text"
                            value={vehicleForm.brand}
                            onChange={(e) => setVehicleForm({...vehicleForm, brand: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder="Marque"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Modèle</label>
                          <input
                            type="text"
                            value={vehicleForm.model}
                            onChange={(e) => setVehicleForm({...vehicleForm, model: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder="Modèle"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Année</label>
                          <input
                            type="number"
                            value={vehicleForm.year}
                            onChange={(e) => setVehicleForm({...vehicleForm, year: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder="2024"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Tarif horaire (€)</label>
                          <input
                            type="number"
                            step="0.01"
                            value={vehicleForm.hourly_rate}
                            onChange={(e) => setVehicleForm({...vehicleForm, hourly_rate: e.target.value})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                            placeholder="10.00"
                          />
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="is_available"
                          checked={vehicleForm.is_available}
                          onChange={(e) => setVehicleForm({...vehicleForm, is_available: e.target.checked})}
                          className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                        />
                        <label htmlFor="is_available" className="text-sm text-gray-700">
                          Disponible pour la location
                        </label>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Position du véhicule</label>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
                          <button
                            type="button"
                            onClick={() => setShowLocationPicker(true)}
                            className="flex items-center space-x-2 px-3 sm:px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-sm w-full sm:w-auto"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                            </svg>
                            <span>Sélectionner sur la carte</span>
                          </button>
                          
                          {(vehicleForm.latitude && vehicleForm.longitude) && (
                            <div className="text-sm text-gray-600 w-full sm:w-auto">
                              <span className="font-medium">Position :</span>
                              <br />
                              <span className="font-mono text-xs break-all">
                                {parseFloat(vehicleForm.latitude).toFixed(6)}, {parseFloat(vehicleForm.longitude).toFixed(6)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-3 mt-6">
                      <button
                        onClick={closeVehicleForm}
                        className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={() => {
                          console.log('🖱️ Bouton "Modifier/Ajouter" cliqué !');
                          console.log('🔍 editingVehicle:', editingVehicle);
                          console.log('🔍 vehicleForm:', vehicleForm);
                          saveVehicle();
                        }}
                        className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                      >
                        {editingVehicle ? 'Modifier' : 'Ajouter'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* LocationPicker Modal
              {showLocationPicker && (
                <LocationPicker
                  onLocationSelect={handleLocationSelect}
                  initialLat={vehicleForm.latitude ? parseFloat(vehicleForm.latitude) : undefined}
                  initialLng={vehicleForm.longitude ? parseFloat(vehicleForm.longitude) : undefined}
                  vehicleType={vehicleForm.vehicle_type}
                  onClose={() => setShowLocationPicker(false)}
                />
              )}
          </div>

          {/* Liste des véhicules 
          <div className="p-3 sm:p-6">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {[1, 2, 3].map((index) => (
                  <div key={index} className="bg-gray-100 rounded-lg p-3 sm:p-4 animate-pulse">
                    <div className="h-20 sm:h-24 bg-gray-200 rounded-t-lg mb-3 sm:mb-4"></div>
                    <div className="h-3 sm:h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-2 sm:h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-2 sm:h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : userVehicles.length === 0 ? (
              <div className="text-center py-8 sm:py-12 px-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                  </svg>
                </div>
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Aucun véhicule</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-4">Commencez par ajouter votre premier véhicule à votre flotte</p>

              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3 sm:gap-4 max-h-96 overflow-y-auto">
                {userVehicles.map((vehicle, index) => {
                  const icon = getVehicleIcon(vehicle.vehicle_type);
                  const status = vehicle.is_available ? 'disponible' : 'occupé';
                  const title = vehicle.brand && vehicle.model ? `${vehicle.brand} ${vehicle.model}` : vehicle.serial_number || `Mobilité #${vehicle.id_vehicle || index + 1}`;
                  const price = vehicle.hourly_rate ? `${Number(vehicle.hourly_rate).toFixed(2)}€/h` : 'Prix non défini';
                  
                  return (
                    <div
                      key={vehicle.id_vehicle || index}
                      className="w-full rounded-lg shadow-md transition-all duration-200 mobility-card bg-white hover:shadow-lg cursor-pointer"
                    >
                      {/* Image du véhicule
                      <div className="relative h-20 sm:h-24 rounded-t-lg flex items-center justify-center bg-gradient-to-br from-indigo-100 to-purple-100">
                        <div className="text-2xl sm:text-3xl">
                          {icon}
                        </div>
                      </div>
                      
                      {/* Contenu de la carte 
                      <div className="p-2 sm:p-2">
                        {/* Titre et statut 
                        <div className="flex items-start justify-between mb-1">
                          <h4 className="font-semibold text-gray-900 text-xs sm:text-sm leading-tight flex-1 pr-2">{title}</h4>
                          <span className={`inline-flex px-1.5 sm:px-2 py-0.5 sm:py-1 text-xs font-semibold rounded-full ${
                            status === 'disponible' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                          }`}>
                            {status}
                          </span>
                        </div>
                        
                        {/* Étoiles de notation 
                        <div className="flex items-center mb-1">
                          <div className="flex space-x-0.5">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <svg key={star} className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                              </svg>
                            ))}
                          </div>
                          <span className="text-xs text-gray-500 ml-1">4.8 (127)</span>
                        </div>
                        
                        {/* Informations détaillées 
                        <div className="space-y-0.5 text-xs text-gray-600 mb-2">
                          <div className="flex items-center justify-between">
                            <span className="flex items-center space-x-1">
                              <svg className="w-2.5 h-2.5 sm:w-3 sm:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                              </svg>
                              <span className="truncate">
                                {vehicle.latitude && vehicle.longitude 
                                  ? (cities[`${vehicle.latitude},${vehicle.longitude}`] || `${Number(vehicle.latitude).toFixed(2)}, ${Number(vehicle.longitude).toFixed(2)}`)
                                  : 'N/D'
                                }
                              </span>
                            </span>
                            <span className="font-semibold text-indigo-600 text-xs sm:text-xs">{price}</span>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <span className="text-gray-500 text-xs">Type: {getVehicleTypeInFrench(vehicle.vehicle_type)}</span>
                            <span className="text-gray-500 text-xs">ID: {vehicle.serial_number ? vehicle.serial_number.slice(-6) : vehicle.id_vehicle}</span>
                          </div>
                        </div>
                        
                        {/* Boutons d'action 
                        <div className="flex space-x-1">
                          <button
                            onClick={() => openEditVehicle(vehicle)}
                            className="flex-1 px-1.5 sm:px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 transition-colors"
                            title="Modifier"
                          >
                            <span className="hidden sm:inline">Modifier</span>
                            <span className="sm:hidden">Mod.</span>
                          </button>
                          <button
                            onClick={() => deleteVehicle(vehicle.id_vehicle)}
                            className="px-1.5 sm:px-2 py-1 bg-red-600 text-white text-xs rounded hover:bg-red-700 transition-colors"
                            title="Supprimer"
                          >
                            <svg className="w-3 h-3 sm:w-3 sm:h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
};*/

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('dashboard');
  const sectionAllowed = canOpenSection(user?.role, activeSection);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Synchroniser activeSection avec l'URL
  useEffect(() => {
    const path = location.pathname.split('/').pop() || 'dashboard';
    // Explorer, Messages et Avis (standalone) sont désactivés : un lien ou
    // signet vers ces routes retombe sur le tableau de bord plutôt que
    // d'afficher une page morte. « promo-codes » est la section des codes
    // promo des propriétaires.
    if (['overview', 'comments', 'reviews'].includes(path)) {
      navigate('/dashboard', { replace: true });
      return;
    }
    setActiveSection(path);
  }, [location, navigate]);

  // Fonction pour obtenir le titre selon la section active
  const getTitle = () => {
    switch (activeSection) {
      case 'dashboard': return 'Tableau de bord';
      case 'mytrips': return 'Trajets';
      case 'myrentals': return 'Flotte';
      case 'analytics': return 'Analytics';
      case 'frais': return 'Frais';
      case 'promo-codes': return 'Codes promo';
      case 'communication': return 'Communication';
      case 'support': return 'Support';
      case 'journaux': return 'Journaux';
      case 'user': return 'Mon Profil';
      case 'settings': return 'Paramètres';
      case 'users': return 'Utilisateurs';
      case 'verifications': return 'Vérifications';
      case 'a-traiter': return 'À traiter';
      // Le repli disait « Dashboard » en anglais, seul mot non traduit de
      // l'interface, et masquait qu'une section n'avait pas été déclarée ici.
      default: return SECTION_LABELS[activeSection] || 'Tableau de bord';
    }
  };

  // Fonction pour obtenir la description selon la section active
  const getSubtitle = () => {
    switch (activeSection) {
      case 'dashboard': return 'Voici ce qui se passe sur Dingo aujourd’hui.';
      case 'discover': return 'Gérez votre flotte personnelle';
      case 'mytrips': return 'Consultez et gérez les trajets';
      case 'myrentals': return 'Gérez la flotte de mobilités';
      case 'analytics': return 'Ce que deviennent les demandes, et comment l’app est utilisée (Android, iOS, écrans)';
      case 'frais': return 'Les frais Dingo des nouvelles locations, et ce que doivent les propriétaires';
      case 'promo-codes': return 'Des remises sur ce que les propriétaires reversent à Dingo';
      case 'communication': return 'Ce que Dingo envoie aux utilisateurs : campagnes à valider, push, modèles d\'e-mail';
      case 'support': return 'Tickets, litiges, discussions avec les utilisateurs et réponses à l’aide de l’app';
      case 'journaux': return 'Appels reçus par l\'API et erreurs renvoyées, historique des actions sensibles du back-office';
      case 'user': return 'Gérez vos informations personnelles';
      case 'settings': return 'Configurez vos préférences et paramètres';
      case 'users': return 'Gérez les comptes et les suspensions';
      case 'verifications': return 'Traitez les documents déposés par les utilisateurs';
      case 'a-traiter': return 'Tout ce qui attend une action de votre part';
      // Pas de repli inventé : une section sans sous-titre n'en affiche plus,
      // plutôt que d'hériter de celui du tableau de bord.
      default: return undefined;
    }
  };

  // Fonction pour naviguer vers une section
  const handleSectionChange = (section: string) => {
    setActiveSection(section);
    navigate(`/${section}`);
  };

  return (
    <div className="flex h-screen bg-[#F7F7FB]">
      {/* Sidebar */}
      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        onSectionChange={handleSectionChange}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:ml-0 overflow-hidden">
        {/* Header */}
        {/* Sur une section refusée, l'en-tête n'annonce pas son titre : afficher
            « Paiements — Consultez l'historique » au-dessus d'un refus laisse
            croire qu'on y est, et décrit un contenu qu'on ne verra pas. */}
        <Header
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          title={sectionAllowed ? getTitle() : 'Accès refusé'}
          subtitle={sectionAllowed ? getSubtitle() : 'Votre rôle ne donne pas accès à cette section.'}
        />

        {/* Une section fermée à son rôle ne doit pas s'ouvrir en tapant l'URL :
            masquer l'entrée dans la barre latérale ne suffit pas. */}
        {!sectionAllowed && (
          <AccessDenied section={SECTION_LABELS[activeSection]} role={user?.role} />
        )}

        {/* Content - Vue dynamique selon la section active */}
        {sectionAllowed && (
          <>
        {activeSection === 'dashboard' && <Dashboard />}
        {/*activeSection === 'discover' && <DiscoverView />*/}
        {activeSection === 'mytrips' && <TripsView />}
        {activeSection === 'myrentals' && <FleetView />}
        {activeSection === 'analytics' && <ManagementAnalyticsHub />}
        {activeSection === 'frais' && <ManagementFees />}
        {activeSection === 'promo-codes' && <ManagementOwnerPromos />}
        {activeSection === 'communication' && <ManagementCommunicationHub />}
        {activeSection === 'support' && <ManagementSupport />}
        {activeSection === 'journaux' && <ManagementLogsHub />}
        {activeSection === 'user' && <UserProfile />}
        {activeSection === 'settings' && <Settings />}
        {activeSection === 'users' && <ManagementUsers />}
        {activeSection === 'verifications' && <ManagementVerifications />}
        {activeSection === 'a-traiter' && <ManagementPendingQueue />}
          </>
        )}
      </div>
    </div>
  );
};

export default Layout; 